import {
  ORIGIN_LABEL, billingPartyOf, brandDiscountCents, brandDiscountFromRow, brandNetLines, creatorShareLines, extraInPeriod, checkInvoiceRequest, computeDealSplit, computeInvoiceTotals, computeReferral, dealModelName,
  dealModelTitle, dealRevenue, dealRevenueStarted, deriveDealStatus, deriveMonthStatus, deriveSettlement, detectExpenseLikeLines, effectiveCommissionPercent,
  effectiveRoute, hasOpenInvoice, inCollection, invoiceFromRow, isCancelled, isDealActive, isFromCreator, isLive, isPaid, isToBrand, referralFromRow,
  resolveCommissionRate, dealBpForMonth, dealOccurrences, overviewDealFromRow, overviewInvoiceFromRow, overviewPeriodFromRow, postTermText, validPostTermRule, settlementKinds, tiersFromJson, DEAL_MODEL_KEYS, MONTH_OUTCOME_LABEL, MONTH_OUTCOME_LINE, OUTCOMES_WITH_FEE,
  type AgencyFee, type BillingModel, type BrandDiscount, type CommissionTier, type DealCatalog, type DealMonth, type DealRevenue, type DealSplit,
  type DealRowForOverview, type InvoiceRowForOverview, type OverviewFee, type InvoiceRecord, type InvoiceRoute, type InvoiceRow as DomainInvoiceRow, type MonthBooking, type MonthSettlement, type ReferralMonth, type ReferralTerms,
  type RequestCheckCode, type ResolvedCommission, type SplitLine,
} from "@talentkontor/domain";
import type { Pill, Tone } from "../../../model/invoice-view";
import { date as tag, monthLabel, monthName, numberLocale, today as heuteLokal } from "../../../lib/format";
import type {
  BrandRow, CampaignRow, CatalogEntry, ContractLite, DealFull, DealModel, DealStatus, HistoryRow, InvoicePeriodRow, InvoiceRow, ItemKind, ItemRow,
  KoopDaten, MessageLite, PeriodFull, SettingsLite, StatusLabel, SubmissionRow, Substep, TalentRow, TermsRow,
} from "./typen";
import { t, lazyRecord, currentLang, studioText } from "../../../lib/sprache";

/**
 * Reine Anzeige- und Rechenlogik der Kooperationen (Liste, Board, Ansicht nach Marke, Detail, Content-Prüfung).
 * Fachregeln kommen aus @talentkontor/domain (Monatsstatus KA-10, Deal-Status A-14/KA-14, aktiv trotz Endstatus KA-12,
 * Buchung KA-05, Aufteilung KZ-01 … KZ-09, Anforderungsprüfung ST-21/KA-18). Hier wird nur zusammengesetzt und beschriftet.
 */

// ── Beschriftungen ─────────────────────────────────────────────────────────

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
export const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Filtergruppen wie im bisherigen Portal (AD-041): zu tun, in Bearbeitung, in Abrechnung, erledigt. */
export type Bucket = "todo" | "in_progress" | "billing" | "done";
export const BUCKET_LABEL: Record<Bucket, string> = { get todo() { return t("Zu tun", "To do"); }, get in_progress() { return t("In Bearbeitung", "In progress"); }, get billing() { return t("In Abrechnung", "In invoicing"); }, get done() { return t("Erledigt", "Done"); } };

interface StatusDef {
  label: string;
  tone: Tone;
  bucket: Bucket;
  /** Dringlichkeit für die Sortierung „Status“ (klein = zuerst), wie ADM_STATUS_RELEVANCE im bisherigen Portal */
  rank: number;
  terminal?: "completed" | "blocked" | "cancelled";
}

/** Arbeitsstand (deal_status) im Statusmodell des Design-Pakets (03_tokens/status.json, s01–s14). */
export const DEAL_STATUS: Record<DealStatus, StatusDef> = {
  planning: { get label() { return t("In Planung", "In planning"); }, tone: "neutral", bucket: "todo", rank: 14 },
  contract: { get label() { return t("Vertrag zur Unterschrift", "Contract awaiting signature"); }, tone: "info", bucket: "todo", rank: 13 },
  concept: { get label() { return t("Konzept ausstehend (Creator)", "Concept pending (creator)"); }, tone: "warning", bucket: "in_progress", rank: 6 },
  production: { get label() { return t("In Produktion", "In production"); }, tone: "info", bucket: "in_progress", rank: 8 },
  content_approval: { get label() { return t("Freigabe Content", "Content approval"); }, tone: "warning", bucket: "in_progress", rank: 7 },
  published: { get label() { return t("Veröffentlicht", "Published"); }, tone: "success", bucket: "in_progress", rank: 9 },
  reporting: { label: "Reporting", tone: "info", bucket: "billing", rank: 12 },
  // Arbeitsstand „Abrechnung vorbereiten“; „Rechnung ausstehend“ heißt nur die echte Anforderung beim Creator (Befund 25)
  invoicing: { get label() { return t("Abrechnung vorbereiten", "Prepare invoicing"); }, tone: "warning", bucket: "billing", rank: 4 },
  done: { get label() { return t("Abgeschlossen", "Completed"); }, tone: "success", bucket: "done", rank: 17, terminal: "completed" },
  blocked: { get label() { return t("Blockiert", "Blocked"); }, tone: "error", bucket: "todo", rank: 3, terminal: "blocked" },
  cancelled: { get label() { return t("Storniert", "Cancelled"); }, tone: "neutral", bucket: "done", rank: 18, terminal: "cancelled" },
};
export const DEAL_STATUS_ORDER: DealStatus[] = ["planning", "contract", "concept", "production", "content_approval", "published", "reporting", "invoicing", "done", "blocked", "cancelled"];

/**
 * D2-1 (21.10.2026): Kurzform langer Statuspillen für schmale Listen (der volle Text steht im Tooltip). Pillen brechen nie um;
 * passt die Liste nicht, zeigt sie die Kurzform statt die Spalte zu verbreitern. null: der Text ist schon kurz.
 */
export function statusKurz(label: string): string | null {
  const paare: [string, string][] = [
    [t("Konzept ausstehend (Creator)", "Concept pending (creator)"), t("Konzept offen", "Concept open")],
    [t("Vertrag zur Unterschrift", "Contract awaiting signature"), t("Zur Unterschrift", "To sign")],
    [t("Abrechnung vorbereiten", "Prepare invoicing"), t("Abrechnung", "Invoicing")],
    [t("Rechnung an Marke versendet", "Invoice sent to brand"), t("An Marke versendet", "Sent to brand")],
    [t("Markenrechnung storniert", "Brand invoice cancelled"), t("Rechnung storniert", "Invoice cancelled")],
    [t("Marke bezahlt · Creator offen", "Brand paid · creator open"), t("Creator offen", "Creator open")],
    [t("Marke bezahlt · Provision offen", "Brand paid · commission open"), t("Provision offen", "Commission open")],
    [t("Creator ausgezahlt · Marke offen", "Creator paid out · brand open"), t("Marke offen", "Brand open")],
    [t("Nicht abgenommen · gemindert", "Not accepted · reduced"), t("Gemindert", "Reduced")],
    [t("Nicht abgenommen · Ausfallhonorar", "Not accepted · cancellation fee"), t("Ausfallhonorar", "Cancellation fee")],
    [t("Abgerechnet (übernommen)", "Invoiced (taken over)"), t("Abgerechnet", "Invoiced")],
    [t("Marke bezahlt (übernommen)", "Brand paid (taken over)"), t("Marke bezahlt", "Brand paid")],
  ];
  return paare.find(([lang]) => lang === label)?.[1] ?? null;
}

/** Eine Seite bezahlt, die andere offen (Marke bezahlt, Creator bzw. Provision offen, oder umgekehrt) */
export const TEILWEISE: ReadonlySet<string> = new Set(["partially_paid", "partially_paid_commission", "creator_paid_brand_open"]);

/** Aus Rechnungen abgeleitete Stände (deriveMonthStatus/deriveDealStatus) und Sonderfälle der Anzeige. */
const DERIVED: Record<string, StatusDef> = {
  // Content freigegeben, Veröffentlichung (Datum, Link) noch nicht eingetragen (Befund 12)
  content_approved: { get label() { return t("Content freigegeben", "Content approved"); }, tone: "info", bucket: "in_progress", rank: 8 },
  open: { get label() { return t("Offen", "Open"); }, tone: "neutral", bucket: "todo", rank: 15 },
  invoice_requested: { get label() { return t("Rechnung ausstehend", "Invoice pending"); }, tone: "warning", bucket: "billing", rank: 4 },
  action_required: { get label() { return t("Korrektur nötig", "Correction needed"); }, tone: "warning", bucket: "billing", rank: 1 },
  invoice_submitted: { get label() { return t("Rechnung in Prüfung", "Invoice in review"); }, tone: "warning", bucket: "billing", rank: 5 },
  invoice_approved: { get label() { return t("Rechnung freigegeben", "Invoice approved"); }, tone: "info", bucket: "billing", rank: 11 },
  invoice_at_customer: { get label() { return t("Rechnung an Marke versendet", "Invoice sent to brand"); }, tone: "info", bucket: "billing", rank: 10 },
  // Rechnung an die Marke storniert, keine neue gestellt (Praxistest K-138)
  brand_invoice_cancelled: { get label() { return t("Markenrechnung storniert", "Brand invoice cancelled"); }, tone: "warning", bucket: "billing", rank: 3 },
  // Rechnungsmail an die Marke zurückgekommen, nicht zugegangen: neu zustellen (GELD-21; derselbe Stand wie „Stand je Bereich“)
  delivery_failed: { get label() { return t("Versand gescheitert", "Delivery failed"); }, tone: "error", bucket: "billing", rank: 2 },
  paid: { get label() { return t("Bezahlt", "Paid"); }, tone: "success", bucket: "billing", rank: 16 },
  // Eine Seite bezahlt, die andere offen (Marke zahlt vor der Creator-Rechnung; Provision an den Creator offen)
  // Praxistest K-147 a: „Teilweise bezahlt“ las sich wie eine Teilzahlung der Marke; jetzt steht da, welche Seite offen ist
  partially_paid: { get label() { return t("Marke bezahlt · Creator offen", "Brand paid · creator open"); }, tone: "info", bucket: "billing", rank: 9 },
  partially_paid_commission: { get label() { return t("Marke bezahlt · Provision offen", "Brand paid · commission open"); }, tone: "info", bucket: "billing", rank: 9 },
  creator_paid_brand_open: { get label() { return t("Creator ausgezahlt · Marke offen", "Creator paid out · brand open"); }, tone: "warning", bucket: "billing", rank: 9 },
  // Rechnung an die Marke als uneinbringlich ausgebucht: erledigt, aber kein Geldeingang und nicht verbucht (GELD-19)
  bad_debt: { get label() { return t("Forderungsausfall", "Bad debt"); }, tone: "neutral", bucket: "done", rank: 17 },
  collection: { get label() { return t("Inkasso", "Debt collection"); }, tone: "error", bucket: "billing", rank: 2 },
  archived: { get label() { return t("Archiviert", "Archived"); }, tone: "neutral", bucket: "done", rank: 19 },
  // Monatsausgang (0060): abrechenbar mit Ausfallhonorar oder Minderung
  cancellation_fee: { get label() { return t("Ausfallhonorar", "Cancellation fee"); }, tone: "warning", bucket: "billing", rank: 6 },
  not_accepted_reduced: { get label() { return t("Nicht abgenommen · gemindert", "Not accepted · reduced"); }, tone: "warning", bucket: "billing", rank: 6 },
  not_accepted_fee: { get label() { return t("Nicht abgenommen · Ausfallhonorar", "Not accepted · cancellation fee"); }, tone: "warning", bucket: "billing", rank: 6 },
  // Stand aus dem bisherigen System (0064)
  imported_invoiced: { get label() { return t("Abgerechnet (übernommen)", "Invoiced (taken over)"); }, tone: "info", bucket: "done", rank: 17 },
  imported_paid: { get label() { return t("Bezahlt (übernommen)", "Paid (taken over)"); }, tone: "success", bucket: "done", rank: 17 },
  imported_brand_paid: { get label() { return t("Marke bezahlt (übernommen)", "Brand paid (taken over)"); }, tone: "info", bucket: "done", rank: 17 },
};

// ── Monatsausgang (0060) ───────────────────────────────────────────────────

/** Storniert ohne Honorar: zählt nie, keine Rechnung, keine Frist */
export const monatStorniert = (p: Pick<PeriodFull, "status">): boolean => p.status === "cancelled";
/** Ausfallhonorar oder Minderung: bleibt mit diesem Betrag abrechenbar */
export const monatMitAusfall = (p: Pick<PeriodFull, "status" | "outcome" | "cancellation_fee_cents">): boolean =>
  (!!p.outcome && OUTCOMES_WITH_FEE.has(p.outcome)) || (p.cancellation_fee_cents != null && p.status !== "cancelled");
/** Monat mit Ausgang (Storno, Ausfallhonorar, nicht abgenommen): keine Content-Frist mehr */
export const monatAbgeschlossen = (p: Pick<PeriodFull, "status" | "outcome" | "cancellation_fee_cents">): boolean => monatStorniert(p) || monatMitAusfall(p);
/**
 * Logik-Prüfung (Inhaber 02.11.2026), L-04 (wie deal_request_invoice, 0477): Bei einer stornierten Kooperation sperrt die Anforderung
 * nur ein Monat, den das Storno erfasst hat. Monate vor dem Stichtag („ab Monat“) und Monate mit Ausfallhonorar bleiben abzurechnen.
 */
export const anforderungStorniert = (deal: Pick<DealFull, "status">, p: Pick<PeriodFull, "status"> | null | undefined): boolean =>
  deal.status === "cancelled" && (!p || monatStorniert(p));
/** Beschriftung des Ausgangs eines Monats, sonst null */
export function ausgangLabel(p: Pick<PeriodFull, "status" | "outcome" | "cancellation_fee_cents">): string | null {
  if (monatStorniert(p)) return MONTH_OUTCOME_LABEL.cancelled;
  if (p.outcome) return MONTH_OUTCOME_LABEL[p.outcome];
  return p.cancellation_fee_cents != null ? MONTH_OUTCOME_LABEL.cancellation_fee : null;
}
/** Leistungszeile eines Monats: Ausfallhonorar oder Minderung mit Grund, Nachkauf mit Titel, sonst der Leistungsumfang */
export function leistungsText(deal: Pick<DealFull, "deliverables" | "title">, p?: Pick<PeriodFull, "status" | "outcome" | "cancellation_fee_cents" | "cancel_reason" | "kind" | "title"> | null): string {
  const base = deal.deliverables?.trim() || deal.title;
  if (!p) return base;
  if (p.kind === "follow_up") return p.title?.trim() || t("Nachkauf", "Follow-up purchase");
  if (monatMitAusfall(p)) {
    const art = MONTH_OUTCOME_LINE[(p.outcome && p.outcome !== "cancelled" ? p.outcome : "cancellation_fee")];
    return `${art} · ${p.cancel_reason?.trim() || base}`;
  }
  return base;
}

/** Reihenfolge der Board-Spalten (Statusmodell s01 … s15). */
export const BOARD_ORDER = [
  "planning", "contract", "concept", "production", "content_approval", "content_approved", "published", "reporting", "invoicing", "invoice_requested",
  "action_required", "brand_invoice_cancelled", "invoice_submitted", "invoice_approved", "delivery_failed", "invoice_at_customer", "paid", "done", "blocked", "cancelled", "collection", "archived",
];

export type Labels = ReadonlyMap<string, string>;

/**
 * Eigene Bezeichnungen der Agentur (org_status_labels) für die Status-Schlüssel. Agenturen mit englischem Zahlenformat
 * (en-GB) sehen ihre englischen Bezeichnungen, sonst die deutschen (Befund 23).
 */
export function labelMap(labels: readonly StatusLabel[], lang: "de" | "en" = numberLocale().startsWith("en") ? "en" : "de"): Map<string, string> {
  const pick = (l: StatusLabel) => (lang === "en" && l.label_en?.trim() ? l.label_en : l.label_de)?.trim() ?? "";
  return new Map(labels.filter((l) => pick(l)).map((l) => [l.status, pick(l)]));
}
export function hiddenStatuses(labels: readonly StatusLabel[]): Set<string> {
  return new Set(labels.filter((l) => l.hidden).map((l) => l.status));
}

export function statusDef(key: string, labels?: Labels): StatusDef {
  const base = (DEAL_STATUS as Record<string, StatusDef>)[key] ?? DERIVED[key] ?? { label: key, tone: "neutral" as Tone, bucket: "todo" as Bucket, rank: 20 };
  const own = labels?.get(key);
  return own ? { ...base, label: own } : base;
}
export function statusPill(key: string, labels?: Labels): Pill {
  const d = statusDef(key, labels);
  return pill(d.label, d.tone);
}

/** Modellnamen aus dem einen Katalog der Fachlogik (Befund 25: dieselben Namen in Einstellungen, Stammdaten, Formular) */
/** Bezugsgröße der Vermittlerprovision in der Oberflächensprache (die Fachlogik kennt nur die deutsche Bezeichnung) */
export function referralBaseLabel(base: "agency_share" | "budget"): string {
  return base === "budget" ? t("vom Honorar", "of the fee") : t("vom Agenturumsatz (Anteil und Gebühr)", "of the agency revenue (share and fee)");
}
/** Kurzform für die Auswahlliste (SICHT-AGENTUR-GELD-23: die Langform passte am Handy nicht ins Feld); Erklärung darunter */
export function referralBaseOption(base: "agency_share" | "budget"): string {
  return base === "budget" ? t("vom Honorar", "of the fee") : t("vom Agenturumsatz", "of the agency revenue");
}
export const MODEL_ORDER: DealModel[] = [...DEAL_MODEL_KEYS];
export const MODEL_LABEL: Record<DealModel, string> = lazyRecord(MODEL_ORDER, (k) => dealModelName(k, currentLang()));
/** Untertitel der Detailseite („Monatliche Kooperation“) */
export const MODEL_TITLE: Record<DealModel, string> = lazyRecord(MODEL_ORDER, (k) => studioText(dealModelTitle(k, currentLang())));
/** Monats-Lebenszyklus (KA-03): diese Modelle führen eine Zeile je Monat. */
export const MONTH_BASED: ReadonlySet<DealModel> = new Set(["monthly", "installments", "commission_only", "fixed_plus_commission", "custom"]);
/**
 * Kein Umsatz: Event und Gifting (keine Rechnung). Barter zählt mit seinem Honorar wie ein Einmal-Modell, nur der Warenwert
 * nie (Befund 8); ohne Honorar zählt Barter nicht (aufteilung.zaehltNichtAlsUmsatz).
 */
export const NO_REVENUE: ReadonlySet<DealModel> = new Set(["event", "gifting"]);
export const NO_MONEY: ReadonlySet<DealModel> = new Set(["event", "gifting"]);

/** Rückfall, wenn der Katalog der Agentur nicht lesbar ist (0047: Plattformen und Positionsarten je Agentur) */
export const PLATFORM_LABEL: Record<string, string> = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", ugc: "UGC", podcast: "Podcast", twitch: "Twitch", linkedin: "LinkedIn",
  pinterest: "Pinterest", snapchat: "Snapchat", facebook: "Facebook", blog: "Blog", newsletter: "Newsletter", get other() { return t("Weitere", "Other"); },
};
export const PLATFORMS = ["instagram", "tiktok", "youtube", "ugc", "podcast", "other"] as const;

export const ITEM_KIND_LABEL: Record<ItemKind, string> = {
  get usage_rights() { return t("Nutzungsrechte", "Usage rights"); }, spark_ads: "Spark Ads", whitelisting: "Whitelisting", bonus: "Bonus", get cancellation_fee() { return t("Ausfallhonorar", "Cancellation fee"); },
  get revision() { return t("Zusätzliche Korrekturschleife", "Additional revision round"); }, get production() { return t("Produktionskosten", "Production costs"); }, get travel() { return t("Reisekosten", "Travel costs"); }, get products() { return t("Produkte", "Products"); }, get other() { return t("Sonstiges", "Other"); },
};
/** Auslagen gehen 1:1 an den Creator (KZ-04) */
export const ITEM_KIND_COMMISSIONABLE: Record<ItemKind, boolean> = {
  usage_rights: true, spark_ads: true, whitelisting: true, bonus: true, cancellation_fee: true, revision: true, production: false, travel: false, products: false, other: true,
};

const catalogLabel = (e: CatalogEntry) => (numberLocale().startsWith("en") && e.label_en?.trim() ? e.label_en : e.label_de);
export function platformLabel(key: string, catalog: readonly CatalogEntry[] = []): string {
  const e = catalog.find((c) => c.key === key);
  return e ? catalogLabel(e) : PLATFORM_LABEL[key] ?? key;
}
export function itemKindLabel(key: string, catalog: readonly CatalogEntry[] = []): string {
  const e = catalog.find((c) => c.key === key);
  return e ? catalogLabel(e) : ITEM_KIND_LABEL[key] ?? key;
}
export function itemKindCommissionable(key: string, catalog: readonly CatalogEntry[] = []): boolean {
  const e = catalog.find((c) => c.key === key);
  return e?.commissionable ?? ITEM_KIND_COMMISSIONABLE[key] ?? true;
}
/** Auswahl im Formular: aktive Einträge plus die, die die Kooperation schon trägt (abgeschaltete bleiben lesbar). */
export function katalogAuswahl(catalog: readonly CatalogEntry[], fallback: readonly string[], inUse: readonly string[] = []): { key: string; label: string }[] {
  const base = catalog.length
    ? catalog.filter((c) => c.active || inUse.includes(c.key)).map((c) => ({ key: c.key, label: catalogLabel(c) }))
    : fallback.map((k) => ({ key: k, label: PLATFORM_LABEL[k] ?? ITEM_KIND_LABEL[k] ?? k }));
  for (const k of inUse) if (!base.some((b) => b.key === k)) base.push({ key: k, label: PLATFORM_LABEL[k] ?? ITEM_KIND_LABEL[k] ?? k });
  return base;
}

export const CONTRACT_PILL: Record<ContractLite["status"], Pill> = {
  get draft() { return pill(t("Entwurf", "Draft"), "neutral"); },
  get sent() { return pill(t("Zur Unterschrift", "Awaiting signature"), "info"); },
  get partially_signed() { return pill(t("Teilweise signiert", "Partially signed"), "warning"); },
  get signed() { return pill(t("Signiert", "Signed"), "success"); },
  get void() { return pill(t("Aufgehoben", "Rescinded"), "neutral"); },
};

/**
 * Status-Katalog für die Fachlogik (DealCatalog): Endstatus und „zählt als Umsatz“ (KZ-10: ab unterschriebenem Vertrag,
 * also nach „Vertrag zur Unterschrift“), Modelle mit Monats-Lebenszyklus (KA-03) und ohne Umsatz (0016).
 */
export const DEAL_CATALOG: DealCatalog = {
  statuses: DEAL_STATUS_ORDER.map((key) => ({
    key,
    countsAsRevenue: !["planning", "contract", "blocked", "cancelled"].includes(key),
    ...(DEAL_STATUS[key].terminal ? { terminal: DEAL_STATUS[key].terminal } : {}),
  })),
  models: MODEL_ORDER.map((key) => ({ key, revenue: !NO_REVENUE.has(key), monthBased: MONTH_BASED.has(key) })),
};

// ── Hilfen ─────────────────────────────────────────────────────────────────

export { monthName };
/** „September 2026“ */
export const monthText = (key: string): string => monthLabel(key);
/** Tag im Format der Oberflächensprache (31.12.2026 bzw. 31/12/2026) */
function dmy(isoDay: string): string {
  const [y, m, d] = isoDay.slice(0, 10).split("-");
  return currentLang() === "en" ? `${d}/${m}/${y}` : `${d}.${m}.${y}`;
}
const bp = (v: number | null | undefined) => (v == null ? null : v / 100);

export function nameOfTalent(t: Pick<TalentRow, "display_name" | "first_name" | "last_name"> | undefined): string {
  if (!t) return "–";
  return t.display_name?.trim() || `${t.first_name} ${t.last_name ?? ""}`.trim();
}
/** Rufname für kurze Texte („Nachricht an Mara“) */
export function callName(t: (Pick<TalentRow, "first_name"> & { nickname?: string | null }) | undefined): string {
  if (!t) return "Creator";
  const nick = typeof t.nickname === "string" ? t.nickname.trim() : "";
  return nick || t.first_name;
}

/** Nummer der Kooperation wie im Paket („TK-D104“): Kürzel der Agentur, D, laufende Nummer. */
export function dealCode(deal: Pick<DealFull, "deal_number">, prefix: string | null | undefined): string {
  if (deal.deal_number == null) return t("Ohne Nummer", "Without number");
  return prefix ? `${prefix}-D${deal.deal_number}` : `D${deal.deal_number}`;
}

/** Zeitraum „01.09.–30.11.2026“ aus Startmonat und Laufzeit */
export function dealRange(deal: Pick<DealFull, "start_month" | "months">, periods: readonly Pick<PeriodFull, "month">[] = []): string {
  const months = periods.map((p) => p.month.slice(0, 7)).sort();
  const start = months[0] ?? deal.start_month?.slice(0, 7);
  if (!start) return t("Ohne Zeitraum", "Without period");
  const endFromTerm = deal.start_month && deal.months ? addMonthKey(deal.start_month.slice(0, 7), deal.months - 1) : start;
  const last = months[months.length - 1];
  const end = last && last > endFromTerm ? last : endFromTerm;
  const from = `${start}-01`;
  const to = lastDay(end);
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  // Im selben Jahr ohne erstes Jahr: „01.08.–30.11.2026“ bzw. „01/08–30/11/2026“
  return sameYear ? `${dmy(from).slice(0, currentLang() === "en" ? 5 : 6)}–${dmy(to)}` : `${dmy(from)}–${dmy(to)}`;
}
export function addMonthKey(key: string, n: number): string {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(5, 7)) - 1 + n;
  const year = y + Math.floor(m / 12);
  const mon = ((m % 12) + 12) % 12;
  return `${year}-${String(mon + 1).padStart(2, "0")}`;
}
export function lastDay(key: string): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${key}-${String(d).padStart(2, "0")}`;
}

// ── Monate einer Kooperation (KA-10, KA-05, KA-11) ─────────────────────────

export interface InvoiceEntry {
  row: InvoiceRow;
  /** Fachlogik-Datensatz; null, wenn die Zeile nicht lesbar ist (fehlender Stempel) */
  record: InvoiceRecord | null;
}

export interface MonatInfo {
  period: PeriodFull;
  month: string;
  invoices: InvoiceEntry[];
  records: InvoiceRecord[];
  /** Anzeigestatus des Monats (KA-10): Rechnung → Arbeitsstand der Agentur → angefordert → offen */
  status: string;
  source: "invoices" | "manual" | "requested" | "default";
  booking: MonthBooking;
  /** Abrechnungsstand je Seite nach dem Weg der Kooperation (0039): Rechnung an die Marke und Ausgleich mit dem Creator */
  settlement: MonthSettlement;
  /** Monat erledigt: jede nötige Seite bezahlt oder storniert, keine offene Rechnung (KA-11) */
  done: boolean;
  /** Im bisherigen System abgerechnet bzw. bezahlt und ohne Rechnung im Portal (0064) */
  uebernommen?: "invoiced" | "brand_paid" | "paid" | null;
}

export function toRecord(row: InvoiceRow, timeZone: string): InvoiceRecord | null {
  try {
    return invoiceFromRow(row as unknown as DomainInvoiceRow, timeZone);
  } catch {
    return null;
  }
}

/** Rechnung an die Marke (Kundenseite), gleich ob Agentur oder Creator sie stellt. */
export const isBrandInvoice = (row: Pick<InvoiceRow, "kind">) => isToBrand(row.kind);
/** Rechnung des Creators (an die Agentur oder direkt an die Marke). */
export const isCreatorInvoice = (row: Pick<InvoiceRow, "kind">) => isFromCreator(row.kind);

/** Wirksamer Abrechnungsweg: vom Lader gesetzt, sonst Kooperation vor Agentur. */
export function routeOf(deal: Pick<DealFull, "invoice_route" | "route">, settings?: Pick<SettingsLite, "invoice_route"> | null): InvoiceRoute {
  return deal.route ?? effectiveRoute(deal.invoice_route, null, settings?.invoice_route);
}

/**
 * Rechnungen den Monaten zuordnen (Monatszeile, sonst Leistungsmonat) und je Monat die Stände ableiten. Eine Sammelrechnung
 * an die Marke (invoice_periods, 0047) zählt in jedem Monat, den sie abrechnet.
 */
export function monateDerKooperation(deal: DealFull, periods: readonly PeriodFull[], invoices: readonly InvoiceRow[], timeZone: string,
  invoicePeriods: readonly InvoicePeriodRow[] = [], terms?: Pick<TermsRow, "agency_fee_mode"> | null, selbst = false): { monate: MonatInfo[]; ohneMonat: InvoiceEntry[] } {
  // Direkter Weg: Die Agentur stellt dem Creator ihre Provision oder ihre Gebühr in Rechnung, auch bei 0 % mit Gebühr
  // (dieselbe Regel wie app.period_needs in der Datenbank)
  const agencyShare = (deal.commission_bp ?? 0) > 0 || !!tiersFromJson(deal.commission_tiers) || terms?.agency_fee_mode === "percent" || terms?.agency_fee_mode === "fixed";
  const own = [...periods].filter((p) => p.deal_id === deal.id).sort((a, b) => a.month.localeCompare(b.month));
  const byId = new Map(own.map((p) => [p.id, p]));
  const byMonth = new Map(own.map((p) => [p.month.slice(0, 7), p]));
  const lists = new Map<string, InvoiceEntry[]>(own.map((p) => [p.id, []]));
  const ohneMonat: InvoiceEntry[] = [];
  const linked = new Map<string, string[]>();
  for (const l of invoicePeriods) if (byId.has(l.period_id)) linked.set(l.invoice_id, [...(linked.get(l.invoice_id) ?? []), l.period_id]);
  for (const row of invoices) {
    const viaLinks = linked.get(row.id);
    if (viaLinks) {
      const entry = { row, record: toRecord(row, timeZone) };
      for (const pid of viaLinks) lists.get(pid)!.push(entry);
      continue;
    }
    const mine = row.deal_id === deal.id || (row.period_id != null && byId.has(row.period_id));
    if (!mine) continue;
    const entry = { row, record: toRecord(row, timeZone) };
    const p = (row.period_id ? byId.get(row.period_id) : undefined) ?? (row.revenue_month ? byMonth.get(row.revenue_month.slice(0, 7)) : undefined);
    if (p) lists.get(p.id)!.push(entry);
    else ohneMonat.push(entry);
  }
  const monate = own.map((p) => {
    const entries = lists.get(p.id) ?? [];
    const records = entries.map((e) => e.record).filter((r): r is InvoiceRecord => !!r);
    const derived = p.status === "cancelled"
      ? { status: "cancelled", source: "manual" as const }
      : deriveMonthStatus({ invoices: records, manualStatus: p.manual_status, invoiceRequestedAt: p.invoice_requested_at });
    const kinds = settlementKinds({ route: routeOf(deal), model: deal.model, budgetCents: deal.budget_cents, agencyShare, selfEmployed: selbst });
    const settlement = deriveSettlement({
      // Ausgebuchte Rechnung (Forderungsausfall) steht auf „bezahlt“, ist aber kein Geldeingang (GELD-19)
      kinds, invoices: entries.map((e) => ({ kind: e.row.kind, status: e.row.status, paidAt: e.row.paid_at, badDebt: !!(e.row as { bad_debt_at?: string | null }).bad_debt_at, credit: !!e.row.credit_for })),
      invoiceRequestedAt: p.invoice_requested_at, cancelled: p.status === "cancelled", manualBookedAt: p.booked_at,
      // Stand aus dem bisherigen System deckt die Seite ohne Rechnung im Portal (PRAXIS-R3-1, wie app.sync_period_status)
      imported: p.imported_state ?? null,
    });
    // „Verbucht“ = Geld der Marke eingegangen oder von Hand gebucht (eine Regel für Kooperation, Übersicht und Kennzahlen)
    const booking: MonthBooking = { customerPaidAt: settlement.customerPaidAt, talentPaidAt: settlement.creatorPaidAt, booked: settlement.booked };
    // 0064: im bisherigen System abgerechnet bzw. bezahlt, ohne Rechnung im Portal: erledigt, nichts mehr anzufordern
    const uebernommen = !entries.length && p.imported_state ? p.imported_state : null;
    // Eine Seite bezahlt, die andere noch offen oder nicht gestellt: nicht „bezahlt“ anzeigen
    // Praxistest K-138: Die Rechnung an die Marke ist storniert und keine neue gestellt. Solange eine andere Seite (Rechnung des
    // Creators, Auszahlung) besteht, ist das der Stand des Monats: es fehlt die neue Rechnung, nicht „freigegeben“ oder „bezahlt“.
    const markeStorniert = settlement.customer === "none" && p.status !== "cancelled"
      && entries.some((e) => e.row.kind === kinds.customer && e.row.status === "cancelled" && !e.row.credit_for);
    // Eine Seite bezahlt, die andere offen: welche, steht im Stand (K-147 a)
    const teil = settlement.customer === "paid" ? (settlement.kinds.creator === "agency_to_talent" ? "partially_paid_commission" : "partially_paid") : "creator_paid_brand_open";
    const status = settlement.customer === "written_off" ? "bad_debt"
      : markeStorniert && derived.status !== "action_required" ? "brand_invoice_cancelled"
      : derived.status === "paid" && settlement.status !== "paid"
      ? teil
      : derived.status !== "action_required" && settlement.customer === "paid" && settlement.status !== "paid" ? teil : derived.status;
    return {
      period: p,
      month: p.month.slice(0, 7),
      invoices: entries,
      records,
      status,
      source: derived.source,
      booking,
      settlement,
      done: !!uebernommen || ((settlement.status === "paid" || settlement.status === "cancelled") && !hasOpenInvoice(records)),
      uebernommen,
    };
  });
  return { monate, ohneMonat };
}

/**
 * Abrechnungsstand eines Monats, getrennt vom Arbeitsstand: aus Rechnungen, sonst „angefordert“, sonst keiner (null).
 * Der Anzeigestatus nach KA-10 lässt einen gesetzten Arbeitsstand vor die Anforderung treten; hier bleibt sie sichtbar.
 */
export function abrechnungsStand(m: MonatInfo): string | null {
  // Storniert ohne Honorar: nichts abzurechnen (Befund 1)
  if (monatStorniert(m.period)) return "cancelled";
  if (m.uebernommen) return m.uebernommen === "paid" ? "imported_paid" : m.uebernommen === "brand_paid" ? "imported_brand_paid" : "imported_invoiced";
  if (TEILWEISE.has(m.status) || m.status === "bad_debt" || m.status === "brand_invoice_cancelled") return m.status;
  const r = deriveMonthStatus({ invoices: m.records, invoiceRequestedAt: m.period.invoice_requested_at });
  return r.source === "default" ? null : r.status;
}

const toDealMonthRaw = (m: MonatInfo) => toDealMonth(m);
export function toDealMonth(m: MonatInfo, filter?: (e: InvoiceEntry) => boolean): DealMonth {
  const records = filter ? m.invoices.filter(filter).map((e) => e.record).filter((r): r is InvoiceRecord => !!r) : m.records;
  return {
    month: m.month,
    status: m.status,
    invoices: records,
    invoiceRequestedAt: m.period.invoice_requested_at,
    booked: m.booking.booked,
    amountCents: m.period.amount_cents,
  };
}

/** Der Monat, um den es gerade geht: der laufende, sonst der nächste offene, sonst der letzte offene, sonst der letzte. */
export function fokusMonat(monate: readonly MonatInfo[], today: string): MonatInfo | undefined {
  const cur = today.slice(0, 7);
  return monate.find((m) => m.month === cur && !m.done)
    ?? monate.find((m) => !m.done && m.month >= cur)
    ?? [...monate].reverse().find((m) => !m.done)
    ?? monate.find((m) => m.month === cur)
    ?? monate[monate.length - 1];
}

/** Monat aus der Adresse (?monat=YYYY-MM), sonst der Fokusmonat. */
export function gewaehlterMonat(monate: readonly MonatInfo[], param: string | null, today: string): MonatInfo | undefined {
  if (param) {
    const hit = monate.find((m) => m.month === param);
    if (hit) return hit;
  }
  return fokusMonat(monate, today);
}

// ── Stand einer Kooperation (Anzeige) ───────────────────────────────────────

export interface Praesentation {
  key: string;
  pill: Pill;
  bucket: Bucket;
  rank: number;
  /** erledigt im Sinne von AD-041/KA-11/KA-12 (fällt aus „Aktiv“) */
  erledigt: boolean;
  /** Widerspruchs-Hinweis (AD-043, KA-12) neben der Pille „Abgeschlossen“: „1 Monat offen“ */
  hinweis: string | null;
  offeneMonate: number;
  /** Alle wirksamen Rechnungen an die Marke bezahlt (abgeleitet, WS-09) */
  markeBezahlt: boolean;
}

const liveOpen = (e: InvoiceEntry) => !!e.record && isLive(e.record) && !isPaid(e.record) && !isCancelled(e.record);

/**
 * Stand der Kooperation: archiviert → Inkasso (eine Rechnung an die Marke liegt beim Inkasso) → Deal-Status der Fachlogik
 * (Endstatus bleibt stehen, KA-14/A-16; sonst der am wenigsten fortgeschrittene Monat aus Rechnungen und Anforderungen, A-14;
 * ohne beides der Arbeitsstand der Agentur).
 */
export function praesentation(deal: DealFull, alleMonate: readonly MonatInfo[], labels?: Labels, fokus?: MonatInfo): Praesentation {
  // Stornierte Monate bestimmen den Stand nicht mehr (Befund 1); sind alle storniert, ist die Kooperation storniert
  const monate = alleMonate.filter((m) => !monatStorniert(m.period));
  const dm = monate.map((m) => toDealMonth(m));
  const brandOnly = monate.map((m) => toDealMonth(m, (e) => isBrandInvoice(e.row)));
  const markeBezahlt = deriveDealStatus(DEAL_CATALOG, { pipelineStatus: deal.status, months: brandOnly }).brandPaid;
  const make = (key: string, extra: Partial<Praesentation> = {}): Praesentation => {
    const d = statusDef(key, labels);
    return { key, pill: pill(d.label, d.tone), bucket: d.bucket, rank: d.rank, erledigt: false, hinweis: null, offeneMonate: 0, markeBezahlt, ...extra };
  };
  if (deal.archived_at) return make("archived", { bucket: "done", erledigt: true });
  if (alleMonate.length > 0 && monate.length === 0) return make("cancelled", { bucket: "done", erledigt: true });

  const collection = monate.some((m) => m.invoices.some((e) => isBrandInvoice(e.row) && liveOpen(e) && inCollection(e.record!)));
  const active = isDealActive(DEAL_CATALOG, deal.status, dm);
  const terminal = DEAL_STATUS[deal.status]?.terminal;
  if (collection) return make("collection", { offeneMonate: active.openMonths });

  if (terminal === "completed" || terminal === "cancelled") {
    const n = active.openMonths;
    if (!active.active) return make(deal.status, { bucket: "done", erledigt: true });
    // Aktiv trotz Endstatus (KA-12): Gruppe des wichtigsten offenen Monats
    const open = monate.filter((m) => !m.done);
    const top = [...open].sort((a, b) => statusDef(a.status).rank - statusDef(b.status).rank || b.month.localeCompare(a.month))[0];
    return make(deal.status, {
      bucket: top ? (statusDef(top.status).bucket === "done" ? "billing" : statusDef(top.status).bucket) : "billing",
      // nur die Zusatzinfo: der Status selbst steht schon in der Pille daneben (Gesamtprüfung 07.10.2026, Status doppelt)
      hinweis: n === 1 ? t("1 Monat offen", "1 month open") : t(`${n} Monate offen`, `${n} months open`),
      offeneMonate: n,
    });
  }
  if (terminal === "blocked") return make("blocked", { offeneMonate: active.openMonths });

  const derived = deriveDealStatus(DEAL_CATALOG, { pipelineStatus: deal.status, months: dm });
  // Alle vorhandenen Rechnungen bezahlt, aber eine nötige Seite fehlt (Creator-Rechnung, Provision): „teilweise bezahlt“
  const teilMonat = monate.find((m) => TEILWEISE.has(m.status))?.status ?? "partially_paid";
  const res = derived.status === "paid" && monate.some((m) => m.settlement.status !== "paid" && m.settlement.status !== "cancelled" && m.invoices.length > 0)
    ? { ...derived, status: teilMonat }
    : ["invoice_at_customer", "invoice_approved"].includes(derived.status) && monate.some((m) => TEILWEISE.has(m.status)) ? { ...derived, status: teilMonat }
    // Praxistest K-138: stornierte Rechnung an die Marke ohne neue Rechnung geht jedem „versendet“, „freigegeben“ oder „bezahlt“ vor
    : !["action_required", "delivery_failed"].includes(derived.status) && monate.some((m) => m.status === "brand_invoice_cancelled") ? { ...derived, status: "brand_invoice_cancelled" } : derived;
  const single = monate.length <= 1;
  const erledigt = res.status === "paid" && single && monate.every((m) => m.done);
  // Freigegeben, aber noch nicht veröffentlicht: eigene Spalte statt „Veröffentlicht“ (Befund 12)
  const key = res.status === "published" && istFreigegeben(fokus ?? (single ? monate[0] : undefined)) ? "content_approved" : res.status;
  return make(key, { erledigt, bucket: erledigt ? "done" : statusDef(key).bucket, offeneMonate: active.openMonths });
}

/** Content freigegeben, Veröffentlichung (Datum) noch nicht eingetragen. */
export function istFreigegeben(m: MonatInfo | undefined): boolean {
  return !!m && m.period.manual_status === "published" && !!m.period.content_approved_at && !m.period.published_on;
}

/** Anzeige des Leistungsstands: wie leistungsstand, „Veröffentlicht“ ohne Datum nach einer Freigabe heißt „Content freigegeben“. */
export function anzeigeStand(deal: DealFull, monat: MonatInfo | undefined): string {
  const st = leistungsstand(deal, monat);
  return st === "published" && istFreigegeben(monat) ? "content_approved" : st;
}

/**
 * Leistungsstand eines Monats: Bei Modellen mit Monats-Lebenszyklus (KA-03) der Stand des Monats, sonst der Stand der
 * Kooperation (wie im bisherigen Portal: Einzelkooperationen führen einen Status, Monatskooperationen einen je Monat).
 */
export function leistungsstand(deal: DealFull, monat: MonatInfo | undefined): DealStatus {
  // Ausgang des Monats (0060): storniert oder abzurechnen (Ausfallhonorar, Minderung), gleich welcher Arbeitsstand vorher galt
  if (monat && monatStorniert(monat.period)) return "cancelled";
  if (monat && monatMitAusfall(monat.period) && !DEAL_STATUS[deal.status]?.terminal) return "invoicing";
  if (!MONTH_BASED.has(deal.model)) return deal.status;
  return monat?.period.manual_status ?? deal.status;
}

/** Arbeitsstand für Liste und Board: Endstatus der Kooperation, sonst Leistungsstand des Fokusmonats. */
export function arbeitsstand(deal: DealFull, fokus: MonatInfo | undefined): string {
  if (DEAL_STATUS[deal.status]?.terminal) return deal.status;
  return anzeigeStand(deal, fokus);
}

const WORK_STAGES: ReadonlySet<string> = new Set(["planning", "contract", "concept", "production", "content_approval"]);

export interface Frist {
  date: string;
  art: string;
  ueberfaellig: boolean;
}

/** Kalendertag eines Zeitpunkts in der Zone der Agentur ('YYYY-MM-DD'). */
export function tagIn(ts: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ts));
  } catch {
    return ts.slice(0, 10);
  }
}
export function plusTage(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Frist der Creator-Rechnung (Befund 17): Tag der Anforderung plus die eingestellten Tage (org_settings.requested_urgent_days,
 * Standard 14). Dieselbe Regel rechnet der Kalender (calendar_feed); die Übersicht nennt die Rechnung ab dann dringend.
 */
export function creatorFrist(period: Pick<PeriodFull, "invoice_requested_at">, opts: { tage?: number | null; timeZone?: string } = {}): string | null {
  if (!period.invoice_requested_at) return null;
  return plusTage(tagIn(period.invoice_requested_at, opts.timeZone ?? "UTC"), opts.tage ?? 14);
}

/**
 * Nächste Frist: Content-Abgaben in Arbeit, angeforderte Rechnungen des Creators (Anforderung + eingestellte Tage, wie der
 * Kalender der Datenbank), fällige Rechnungen an die Marke, fällige Auszahlungen. Archivierte Kooperationen behalten ihre
 * offenen Posten (Rechnungen, Anforderungen), nur Content-Fristen entfallen (Befund 21).
 */
export function naechsteFrist(deal: DealFull, monate: readonly MonatInfo[], today: string, opts: { tage?: number | null; timeZone?: string } = {}): Frist | null {
  const archiviert = !!deal.archived_at;
  const c: { date: string; art: string }[] = [];
  const terminal = DEAL_STATUS[deal.status]?.terminal;
  // Haben alle Leistungsmonate einen Ausgang (Storno, Ausfallhonorar, nicht abgenommen), gibt es keine Content-Frist mehr (Befund 1)
  const regulaer = monate.filter((m) => m.period.kind !== "follow_up");
  const leistungOffen = regulaer.length === 0 || regulaer.some((m) => !monatAbgeschlossen(m.period));
  if (!archiviert && deal.content_deadline && !terminal && leistungOffen && WORK_STAGES.has(deal.status)) c.push({ date: deal.content_deadline, art: t("Content-Abgabe", "Content delivery") });
  for (const m of monate) {
    const stage = leistungsstand(deal, m);
    if (!archiviert && !m.done && m.period.content_deadline && terminal !== "cancelled" && !monatAbgeschlossen(m.period) && WORK_STAGES.has(stage)) {
      c.push({ date: m.period.content_deadline, art: `Content ${monthName(m.month)}` });
    }
    const submitted = m.invoices.some((e) => isCreatorInvoice(e.row) && e.record && isLive(e.record) && e.record.reviewStatus !== "not_submitted");
    const frist = creatorFrist(m.period, opts);
    // Stornierte Kooperation: nur Monate mit Ausfallhonorar werden noch abgerechnet
    if (frist && !submitted && !m.done && (terminal !== "cancelled" || monatMitAusfall(m.period)) && !monatStorniert(m.period)) {
      c.push({ date: frist, art: t("Rechnung des Creators", "Creator's invoice") });
    }
    for (const e of m.invoices) {
      if (!liveOpen(e) || !e.record) continue;
      const due = e.row.effective_due_date ?? e.row.document_due_date;
      if (!due) continue;
      if (isBrandInvoice(e.row) && e.record.sentToBrandAt) c.push({ date: due, art: t("Zahlung der Marke", "Brand's payment") });
      if (e.row.kind === "talent_to_agency" && e.record.reviewStatus === "approved") c.push({ date: due, art: t("Auszahlung", "Payout") });
      if (e.row.kind === "agency_to_talent" && e.row.status === "sent") c.push({ date: due, art: t("Provision des Creators", "Creator's commission") });
    }
  }
  if (!c.length) return null;
  c.sort((a, b) => a.date.localeCompare(b.date));
  const first = c[0]!;
  return { date: first.date, art: first.art, ueberfaellig: first.date < today };
}

// ── Aufteilung Creator / Agentur (KZ-01 … KZ-09) ────────────────────────────

export interface SplitKontext {
  rate: ResolvedCommission | null;
  fee: AgencyFee | null;
  feeQuelle: "kooperation" | "agentur";
  billing: BillingModel;
  fehler: string | null;
}

export function feeFromRow(mode: string | null | undefined, feeBp: number | null | undefined, cents: number | null | undefined): AgencyFee | null {
  if (mode === "percent" && feeBp != null) return { type: "percent", percent: feeBp / 100 };
  if (mode === "fixed" && cents != null) return { type: "fixed", amountCents: cents };
  return null;
}

export function splitKontext(deal: Pick<DealFull, "commission_bp" | "invoice_route" | "route">, terms: TermsRow | undefined, talent: Pick<TalentRow, "commission_bp"> | undefined, settings: SettingsLite | null): SplitKontext {
  const billing: BillingModel = routeOf(deal, settings) === "direct_to_brand" ? "talent_invoices_brand" : "agency_invoices_brand";
  const fee = terms ? feeFromRow(terms.agency_fee_mode, terms.agency_fee_bp, terms.agency_fee_cents) : feeFromRow(settings?.agency_fee_mode, settings?.agency_fee_bp, settings?.agency_fee_cents);
  const feeQuelle = terms ? "kooperation" : "agentur";
  if (!settings || settings.default_commission_bp == null) {
    // Ohne Agentur-Standard nur mit eigenem Satz rechenbar (Kooperation oder Creator)
    const own = bp(deal.commission_bp) ?? bp(talent?.commission_bp);
    if (own == null) return { rate: null, fee, feeQuelle, billing, fehler: t("Für die Agentur ist kein Standard-Provisionssatz hinterlegt (Einstellungen · Konditionen).", "No default commission rate is on file for the agency (Settings · Terms).") };
    return { rate: resolveCommissionRate({ deal: bp(deal.commission_bp), talent: bp(talent?.commission_bp), agency: own }), fee, feeQuelle, billing, fehler: null };
  }
  return {
    rate: resolveCommissionRate({ deal: bp(deal.commission_bp), talent: bp(talent?.commission_bp), agency: settings.default_commission_bp / 100 }),
    fee, feeQuelle, billing, fehler: null,
  };
}

export const RATE_SOURCE: Record<ResolvedCommission["source"], string> = {
  get deal() { return t("Satz dieser Kooperation", "Rate of this deal"); },
  get talent() { return t("Standard des Creators", "Creator's default"); },
  get agency() { return t("Standard der Agentur", "Agency default"); },
};

/**
 * Herkunft des Satzes für die Anzeige (Befund 28): ein beim Anlegen festgeschriebener Standard heißt so, statt „Satz dieser
 * Kooperation“; mit Staffel die Staffel.
 */
export function satzQuelle(deal: Pick<DealFull, "commission_frozen_from" | "commission_tiers">, rate: ResolvedCommission): string {
  const staffel = tiersFromJson(deal.commission_tiers);
  const frozen = deal.commission_frozen_from;
  if (staffel) return frozen === "talent" ? t("Staffel des Creators, beim Anlegen festgeschrieben", "Creator's tiers, fixed on creation") : t("Staffel dieser Kooperation", "Tiers of this deal");
  if (rate.source === "deal" && frozen === "talent") return t("Standard des Creators, beim Anlegen festgeschrieben", "Creator's default, fixed on creation");
  if (rate.source === "deal" && frozen === "agency") return t("Standard der Agentur, beim Anlegen festgeschrieben", "Agency default, fixed on creation");
  if (rate.source === "deal" && frozen === "origin") return t("Satz der Herkunft, beim Anlegen festgeschrieben", "Rate of the origin, fixed on creation");
  if (rate.source === "deal" && frozen === "post_term") return t("Nachprovision nach Austritt des Creators, festgeschrieben", "Post-term commission after the creator left, fixed");
  return RATE_SOURCE[rate.source];
}

/**
 * Hinweis „Nachprovision“ an einer Kooperation eines ausgetretenen Creators (0074): umgestellte laufende Kooperation (Satz je
 * Abrechnungsmonat), im Nachlauf abgeschlossene (Satz zum Abschlusstag fest), noch angebahnte (Satz beim Abschluss) oder
 * mit eigenem Satz.
 */
export function nachprovisionText(deal: Pick<DealFull, "post_term" | "post_term_from" | "post_term_until" | "commission_bp" | "commission_frozen_from" | "status">): string {
  const bis = deal.post_term_until ? tag(deal.post_term_until) : "";
  const pct = (bp: number) => prozent(bp / 100);
  if (validPostTermRule(deal.post_term) && deal.post_term_from) {
    const heute = heuteLokal();   // Zeitzone des Kontos, nicht UTC (am Monatsersten sonst der Satz des Vormonats)
    const jetzt = dealBpForMonth(deal, heute);
    return t(`Der Creator ist ausgetreten; diese laufende Kooperation rechnet je Abrechnungsmonat nach der Nachlaufregel ab (${postTermText(deal.post_term, pct)}; `, `The creator has left; this ongoing deal is invoiced per invoicing month under the post-term rule (${postTermText(deal.post_term, pct)}; `)
      + t(`im laufenden Monat ${jetzt != null ? pct(jetzt) : "–"}). Regel bis ${bis}, danach ${deal.post_term.after_bp ? pct(deal.post_term.after_bp) : t("keine Agenturprovision", "no agency commission")}.`, `in the current month ${jetzt != null ? pct(jetzt) : "–"}). Rule until ${bis}, then ${deal.post_term.after_bp ? pct(deal.post_term.after_bp) : t("keine Agenturprovision", "no agency commission")}.`);
  }
  if (deal.status === "planning" && deal.commission_frozen_from !== null && deal.commission_frozen_from !== undefined) {
    return t(`Der Creator ist ausgetreten; kommt diese angebahnte Kooperation bis ${bis} zustande, gilt der Satz der Nachlaufregel zum Abschlusstag.`, `The creator has left; if this deal under negotiation is closed by ${bis}, the post-term rate on the closing date applies.`);
  }
  if (deal.commission_frozen_from === "post_term") {
    return t(`Im Nachlauf nach dem Austritt abgeschlossen; Satz der Nachlaufregel zum Abschlusstag festgeschrieben (${deal.commission_bp != null ? pct(deal.commission_bp) : "–"}). Regel gilt bis ${bis}.`, `Closed during the post-term period after leaving; post-term rate on the closing date fixed (${deal.commission_bp != null ? pct(deal.commission_bp) : "–"}). Rule applies until ${bis}.`);
  }
  return t(`Der Creator ist ausgetreten (Nachlaufregel bis ${bis}); diese Kooperation rechnet mit ihrem eigenen Satz ab.`, `The creator has left (post-term rule until ${bis}); this deal is invoiced at its own rate.`);
}

/** Herkunft der Kooperation für Anzeige und Kennzahlen (0074); leer = keine Angabe. */
export function herkunftText(origin: DealFull["origin"] | undefined): string {
  return origin ? ORIGIN_LABEL[origin][currentLang()] : t("Keine Angabe", "Not specified");
}

export interface Summen {
  budgetCents: number;
  talentShare: number;
  agencyShare: number;
  agencyFee: number;
  extraAgencyShare: number;
  managementNet: number;
  brandInvoiceNet: number;
  talentPayoutCents: number | null;
}

export interface MonatsAufteilung {
  month: string;
  periodId: string | null;
  lines: SplitLine[];
  split: DealSplit | null;
  /** Nur Provision: erwarteter Agenturanteil (AD-058) */
  forecastCents: number | null;
  /** Agentur gesamt in diesem Monat: Anteil + Gebühr (+ erwartete Provision) */
  managementCents: number | null;
  /** Satz dieses Monats (bei Staffel der Durchschnittssatz auf das Honorar des Monats) */
  ratePercent: number | null;
  /** Agenturvergütung der Marke in diesem Monat (die Marke zahlt so viel weniger) */
  abzugCents: number;
  /** Monat storniert (ohne Honorar) */
  storniert: boolean;
}

export interface Aufteilung {
  kontext: SplitKontext;
  monate: MonatsAufteilung[];
  gesamt: Summen | null;
  kommissionsfrei: ItemRow[];
  ohneHonorar: boolean;
  zaehltNichtAlsUmsatz: boolean;
  summenprobe: boolean;
  /** Warenwert (Sachleistung): nicht geteilt, keine Rechnung (Befund 15/16) */
  warenwertCents: number;
  /** Vermittlerprovision je Monat (Aufwand der Agentur, Befund 22) */
  vermittler: { terms: ReferralTerms; monate: ReferralMonth[]; gesamtCents: number } | null;
  /** Gestaffelte Provision der Kooperation (0060) */
  staffel: CommissionTier[] | null;
  /** Agenturvergütung der Marke (0060) */
  abzug: BrandDiscount | null;
}

function monthLines(deal: DealFull, amountCents: number | null, extras: readonly ItemRow[], text?: string): SplitLine[] {
  const lines: SplitLine[] = [];
  if (deal.model !== "commission_only" && !NO_MONEY.has(deal.model) && (amountCents ?? 0) > 0) {
    lines.push({ description: text ?? (deal.deliverables?.trim() || deal.title), quantity: 1, unitPriceCents: amountCents!, kind: "fixed" });
  }
  for (const e of extras) {
    if (e.amount_cents <= 0) continue;
    // Zusatzumsatz ist ein eigener Strom: Provision ja, Agenturgebühr nein (KZ-07, KZ-08); Auslagen gehen 1:1 durch (KZ-04)
    lines.push(e.commissionable
      ? { description: e.description, quantity: 1, unitPriceCents: e.amount_cents, kind: "performance" }
      : { description: e.description, quantity: 1, unitPriceCents: e.amount_cents, commissionFree: true });
  }
  return lines;
}

/**
 * Aufteilung je Monat und gesamt (Festbetrag der Gebühr nur im ersten Monat mit Betrag, KZ-07). Stornierte Monate ohne Honorar
 * zählen mit 0 (Befund 1); Staffel und Agenturvergütung der Marke rechnen wie der Rechenkern der Übersicht (0060).
 */
export function aufteilung(deal: DealFull, periods: readonly PeriodFull[], items: readonly ItemRow[], terms: TermsRow | undefined, talent: TalentRow | undefined, settings: SettingsLite | null): Aufteilung {
  const kontext = splitKontext(deal, terms, talent, settings);
  const own = [...periods].filter((p) => p.deal_id === deal.id).sort((a, b) => a.month.localeCompare(b.month));
  const dealItems = items.filter((i) => i.deal_id === deal.id);
  const dealLevel = dealItems.filter((i) => !i.period_id || !own.some((p) => p.id === i.period_id));
  const staffel = tiersFromJson(deal.commission_tiers);
  const abzug = brandDiscountFromRow(deal);
  const rows: { month: string; periodId: string | null; lines: SplitLine[]; forecast: number | null; storniert: boolean }[] = own.length
    ? own.map((p, i) => {
        const storniert = monatStorniert(p);
        return {
          month: p.month.slice(0, 7), periodId: p.id, forecast: storniert ? null : p.commission_forecast_cents, storniert,
          lines: storniert ? [] : monthLines(deal, p.amount_cents, [...dealItems.filter((x) => x.period_id === p.id), ...(i === 0 ? dealLevel : [])], leistungsText(deal, p)),
        };
      })
    : [{ month: (deal.start_month ?? deal.created_at).slice(0, 7), periodId: null, forecast: null, storniert: false, lines: monthLines(deal, deal.budget_cents, dealLevel) }];
  const ohneHonorar = NO_MONEY.has(deal.model) || (deal.model === "barter" && !rows.some((r) => r.lines.length));
  const warenwertCents = own.filter((p) => !monatStorniert(p)).reduce((a, p) => a + (p.goods_value_cents ?? 0), 0);
  const base = { kontext, kommissionsfrei: dealItems.filter((i) => !i.commissionable), ohneHonorar, zaehltNichtAlsUmsatz: NO_REVENUE.has(deal.model) || (deal.model === "barter" && ohneHonorar),
    warenwertCents, staffel, abzug };
  if (!kontext.rate) {
    return { ...base, monate: rows.map((r) => ({ month: r.month, periodId: r.periodId, lines: r.lines, split: null, forecastCents: r.forecast, managementCents: null,
      ratePercent: null, abzugCents: 0, storniert: r.storniert })), gesamt: null, summenprobe: true, vermittler: null };
  }
  const rate = kontext.rate.percent;
  // Honorar eines Monats: provisionspflichtige Positionen mit Betrag (Monatsbetrag und Zusatz); darauf wirken Staffel und Abzug
  const honorarOf = (lines: readonly SplitLine[]) => lines.filter((l) => !l.commissionFree && l.unitPriceCents > 0).reduce((a, l) => a + Math.round(l.unitPriceCents * l.quantity), 0);
  let feeUsed = false;
  const splits = rows.map((r) => {
    const honorar = honorarOf(r.lines);
    const d = abzug ? brandDiscountCents(honorar, abzug) : 0;
    const gemeinsam = d > 0 && abzug?.bearer !== "agency";
    const lines: SplitLine[] = gemeinsam ? [...r.lines, { description: t("Agenturvergütung der Marke", "Brand agency compensation"), quantity: 1, unitPriceCents: -d, kind: "fixed" }] : r.lines;
    // Im Nachlauf nach Austritt (0074): Satz je Abrechnungsmonat nach der Regel
    const nachlauf = deal.post_term ? dealBpForMonth(deal, r.month) : null;
    const pct = nachlauf != null ? nachlauf / 100 : staffel ? effectiveCommissionPercent(honorar - (gemeinsam ? d : 0), { percent: rate, tiers: staffel }) : rate;
    const hasAmount = r.lines.some((l) => l.quantity > 0 && l.unitPriceCents > 0);
    const applyFixedFee = hasAmount && !feeUsed;
    if (applyFixedFee) feeUsed = true;
    let split = computeDealSplit({ lines, commissionPercent: pct, agencyFee: kontext.fee, applyFixedFee, billing: kontext.billing });
    // Trägt die Agentur den Abzug allein, bleibt der Creator-Anteil; die Agentur und die Rechnung an die Marke werden kleiner
    if (d > 0 && !gemeinsam) {
      split = { ...split, agencyShare: split.agencyShare - d, managementNet: split.managementNet - d, brandInvoiceNet: split.brandInvoiceNet - d,
        brandInvoices: split.brandInvoices.map((b) => (b.issuer === "agency" ? { ...b, netCents: b.netCents - d } : b)) };
    }
    return { split, pct, d };
  });
  // Erwartete Provision: bei „Nur Provision“ und „Fest + Provision“ (dort zusätzlich zum Festanteil)
  const withForecast = deal.model === "commission_only" || deal.model === "fixed_plus_commission";
  const monate: MonatsAufteilung[] = rows.map((r, i) => {
    const s = splits[i]!;
    const split = s.split;
    const forecast = withForecast ? (r.forecast ?? 0) : null;
    return { month: r.month, periodId: r.periodId, lines: r.lines, split, forecastCents: withForecast ? r.forecast : null, managementCents: split.managementNet + (forecast ?? 0),
      ratePercent: s.pct, abzugCents: s.d, storniert: r.storniert };
  });
  const sum = (f: (s: DealSplit) => number) => monate.reduce((a, m) => a + (m.split ? f(m.split) : 0), 0);
  const payout = monate.some((m) => m.split?.talentPayoutCents != null) ? monate.reduce((a, m) => a + (m.split?.talentPayoutCents ?? 0), 0) : null;
  const gesamt: Summen = {
    budgetCents: sum((s) => s.budgetCents),
    talentShare: sum((s) => s.talentShare),
    agencyShare: sum((s) => s.agencyShare),
    agencyFee: sum((s) => s.agencyFee),
    extraAgencyShare: sum((s) => s.extraAgencyShare),
    managementNet: sum((s) => s.managementNet) + (withForecast ? monate.reduce((a, m) => a + (m.forecastCents ?? 0), 0) : 0),
    brandInvoiceNet: sum((s) => s.brandInvoiceNet),
    talentPayoutCents: payout,
  };
  const refTerms = referralFromRow(terms);
  let vermittler: Aufteilung["vermittler"] = null;
  if (refTerms.mode !== "none") {
    // Basis „vom Honorar“: was die Marke für Honorar, Zusatz und Auslagen zahlt (nach ihrem Abzug), wie im Rechenkern
    const brutto = (m: MonatsAufteilung) => m.lines.filter((l) => l.unitPriceCents > 0).reduce((a, l) => a + Math.round(l.unitPriceCents * l.quantity), 0) - m.abzugCents;
    const ref = computeReferral(refTerms, monate.map((m) => ({ budgetCents: brutto(m), managementCents: m.managementCents ?? 0 })));
    vermittler = { terms: refTerms, monate: ref, gesamtCents: ref.reduce((a, r) => a + r.referralCents, 0) };
  }
  return { ...base, monate, gesamt, summenprobe: monate.every((m) => !m.split || m.split.sumCheck), vermittler };
}

/** Unterschriebener Vertrag zur Kooperation (Hauptbezug oder Rahmenvertrag). */
export const vertragUnterschrieben = (vertraege: readonly ContractLite[]) => vertraege.some((c) => c.status === "signed");

/** Zählt die Kooperation schon als Umsatz? Nach Einstellung am Arbeitsstand oder am unterschriebenen Vertrag (Befund 27). */
/** Zählt die Kooperation schon als Umsatz? Eine Regel mit Übersicht und Kennzahlen (dealRevenueStarted, Befund 3). */
/**
 * Verständlichkeit B-07: Text im Dialog „Status ändern“. Sagt, dass der Status den Arbeitsstand festhält, das Portal Rechnungs-
 * und Zahlungsstände selbst setzt und ab wann die Kooperation als Umsatz zählt (Einstellung der Agentur).
 */
export function statusDialogText(mode: SettingsLite["revenue_start"] | null | undefined, nurMonat?: string): string {
  const anfang = nurMonat
    ? t(`Die Änderung gilt nur für ${nurMonat}. Hier hältst du fest, wie weit die Arbeit ist; Rechnungs- und Zahlungsstände setzt das Portal selbst aus den Rechnungen.`, `The change only applies to ${nurMonat}. Here you record how far the work has progressed; invoice and payment statuses are set by the portal from the invoices.`)
    : t("Hier hältst du fest, wie weit die Arbeit ist; Rechnungs- und Zahlungsstände setzt das Portal selbst aus den Rechnungen.", "Here you record how far the work has progressed; invoice and payment statuses are set by the portal from the invoices.");
  const umsatz = mode === "contract_signed"
    ? t("Als Umsatz zählt die Kooperation, sobald ein verknüpfter Vertrag unterschrieben ist (Einstellung der Agentur).", "The deal counts as revenue as soon as a linked contract is signed (agency setting).")
    : mode
      ? t("Ab dem Arbeitsstand nach „Vertrag zur Unterschrift“ oder mit der ersten endgültig erstellten Rechnung zählt die Kooperation als Umsatz (Einstellung der Agentur).", "From the working status after “Contract for signature” or with the first issued invoice the deal counts as revenue (agency setting).")
      : t("Ab welchem Stand die Kooperation als Umsatz zählt, legt die Agentur unter Einstellungen · Kooperationen fest.", "From which status the deal counts as revenue is set by the agency under Settings · Deals.");
  const storno = nurMonat ? "" : " " + t("Für einen Storno mit Ausfallhonorar „Kooperation stornieren“ nutzen.", "For a cancellation with a cancellation fee use “Cancel deal”.");
  return `${anfang} ${umsatz}${storno}`;
}

export function umsatzBegonnen(deal: Pick<DealFull, "status">, opts: { mode?: SettingsLite["revenue_start"]; unterschrieben?: boolean; abgerechnet?: boolean } = {}): boolean {
  return dealRevenueStarted(deal.status, { mode: opts.mode, signed: opts.unterschrieben, activity: opts.abgerechnet });
}

/** Schon etwas festgeschrieben abgerechnet (gleich welche Seite, ohne Stornorechnungen)? Dann hat die Kooperation begonnen (K-148). */
export function schonAbgerechnet(monate: readonly MonatInfo[]): boolean {
  return monate.some((m) => m.invoices.some((e) => ["approved", "sent", "paid"].includes(e.row.status) && !e.row.credit_for));
}

/**
 * Kooperationsumsatz: verbucht und voraussichtlich (KZ-10, KZ-11, KA-13, KA-21), Umsatzbeginn nach Einstellung der Agentur.
 * Stornierte Monate zählen nie; bei stornierter Kooperation zählen Ausfallhonorare wie laufende Monate (Rechenkern der Übersicht).
 */
export function umsatz(deal: DealFull, monate: readonly MonatInfo[], opts0: { mode?: SettingsLite["revenue_start"]; unterschrieben?: boolean } = {}): DealRevenue {
  const opts = { ...opts0, abgerechnet: schonAbgerechnet(monate) };
  const terminal = DEAL_STATUS[deal.status]?.terminal;
  // Die Marke zahlt das Honorar nach ihrem Abzug (Agenturvergütung der Marke): so zählen es auch Aufteilung und Kennzahlen (GELD-16)
  const abzug = brandDiscountFromRow(deal);
  const toDealMonth = (m: MonatInfo): DealMonth => {
    const x = toDealMonthRaw(m);
    return abzug && x.amountCents != null ? { ...x, amountCents: x.amountCents - brandDiscountCents(x.amountCents, abzug) } : x;
  };
  const lebend = monate.filter((m) => !monatStorniert(m.period));
  // Ausfallhonorare zählen immer (vereinbarter Umsatz), wie im Rechenkern
  if (!terminal && !umsatzBegonnen(deal, opts) && lebend.some((m) => monatMitAusfall(m.period))) {
    return dealRevenue(DEAL_CATALOG, { modelKey: deal.model, status: "production", months: lebend.filter((m) => m.booking.booked || monatMitAusfall(m.period)).map((m) => toDealMonth(m)) });
  }
  if (terminal === "cancelled") {
    const ausfall = lebend.filter((m) => monatMitAusfall(m.period) || m.booking.booked);
    return dealRevenue(DEAL_CATALOG, { modelKey: deal.model, status: "production", months: ausfall.map((m) => toDealMonth(m)) });
  }
  // Vor dem Umsatzbeginn (Arbeitsstand oder fehlende Unterschrift) zählen nur verbuchte Monate, wie im Rechenkern
  if (!terminal && !umsatzBegonnen(deal, opts)) {
    return dealRevenue(DEAL_CATALOG, { modelKey: deal.model, status: "production", months: lebend.filter((m) => m.booking.booked).map((m) => toDealMonth(m)) });
  }
  const status = terminal || DEAL_CATALOG.statuses.find((s) => s.key === deal.status)?.countsAsRevenue ? deal.status : "production";
  return dealRevenue(DEAL_CATALOG, { modelKey: deal.model, status, months: lebend.map((m) => toDealMonth(m)) });
}

/** Umsatz einer Kooperation ohne Monate aus ihren Rechnungen ohne Monat (GELD-24) */
export interface UmsatzOhneMonate {
  bookedCents: number;
  plannedCents: number;
  /** Agenturanteil samt Gebühr (wie in den Kennzahlen) */
  agencyCents: number;
  /** davon als uneinbringlich ausgebucht (Forderungsausfall): steht weder als verbucht noch als voraussichtlich */
  writtenOffCents: number;
  raten: number;
}

/**
 * Kooperation ohne Monate, abgerechnet über Rechnungen ohne Monat (Import, Rechnung ohne gewählten Monat): dieselbe Regel wie die
 * Kennzahlen (dealOccurrences, GELD-11/GELD-18). Jede wirksame Rechnung an die Marke ist eine Rate; verbucht, sobald sie bezahlt
 * ist (ohne Forderungsausfall), sonst voraussichtlich. Ohne solche Rechnungen null.
 */
export function umsatzOhneMonate(deal: DealFull, ohneMonat: readonly InvoiceEntry[], ctx: {
  settings: SettingsLite | null; terms?: TermsRow; talent?: Pick<TalentRow, "id" | "commission_bp">; today: string; unterschrieben?: boolean;
}): UmsatzOhneMonate | null {
  const fee = (mode: string | null | undefined, bp: number | null | undefined, cents: number | null | undefined): OverviewFee =>
    mode === "percent" && bp != null ? { mode: "percent", percent: bp / 100 } : mode === "fixed" && cents != null ? { mode: "fixed", cents } : { mode: "none" };
  const s = ctx.settings;
  const rows = ohneMonat.map((e) => e.row as unknown as InvoiceRowForOverview);
  const occ = dealOccurrences({
    today: ctx.today, from: ctx.today.slice(0, 7), to: ctx.today.slice(0, 7), currency: deal.currency, orgCurrency: s?.currency ?? deal.currency,
    agencyCommissionPercent: (s?.default_commission_bp ?? 0) / 100,
    talentCommissionPercent: ctx.talent ? { [ctx.talent.id]: ctx.talent.commission_bp == null ? null : ctx.talent.commission_bp / 100 } : {},
    agencyFee: fee(s?.agency_fee_mode, s?.agency_fee_bp, s?.agency_fee_cents),
    deals: [overviewDealFromRow(deal as unknown as DealRowForOverview, {
      fee: ctx.terms ? fee(ctx.terms.agency_fee_mode, ctx.terms.agency_fee_bp, ctx.terms.agency_fee_cents) : null, terms: ctx.terms ?? null,
      brandRoute: routeOf(deal, s), orgRoute: s?.invoice_route ?? null, revenueStart: s?.revenue_start ?? null, signed: ctx.unterschrieben,
    })],
    periods: [], extras: [], invoices: rows.map((r) => overviewInvoiceFromRow(r)),
  }).filter((o) => o.dealId === deal.id && o.periodId.startsWith("invoice:") && !Number.isNaN(o.budgetCents));
  if (!occ.length) return null;
  const bad = new Set(rows.filter((r) => r.bad_debt_at).map((r) => `invoice:${r.id}`));
  const live = occ.filter((o) => !bad.has(o.periodId));
  const sum = (list: typeof occ) => list.reduce((a, o) => a + o.budgetCents, 0);
  return {
    bookedCents: sum(live.filter((o) => o.booked)), plannedCents: sum(live.filter((o) => !o.booked)), writtenOffCents: sum(occ.filter((o) => bad.has(o.periodId))),
    agencyCents: live.reduce((a, o) => a + o.managementCents, 0), raten: occ.length,
  };
}

/**
 * Kooperationsumsatz wie in den Kennzahlen (Nachtrag K-147 b): Honorar, Zusatzpositionen und Agenturgebühr abzüglich des Abzugs der
 * Marke, je Monat verbucht oder voraussichtlich, aus demselben Rechenkern (dealOccurrences). Vorher zählte die Zeile auf der
 * Kooperationsseite nur das Honorar nach Abzug (NT-D20: 17.000,01 € gegen 20.149,38 € in den Kennzahlen).
 */
export function umsatzWieKennzahlen(deal: DealFull, periods: readonly PeriodFull[], items: readonly ItemRow[], invoices: readonly InvoiceRow[], ctx: {
  settings: SettingsLite | null; terms?: TermsRow; talent?: Pick<TalentRow, "id" | "commission_bp">; today: string; unterschrieben?: boolean;
}): { bookedCents: number; plannedCents: number } | null {
  const fee = (mode: string | null | undefined, bp: number | null | undefined, cents: number | null | undefined): OverviewFee =>
    mode === "percent" && bp != null ? { mode: "percent", percent: bp / 100 } : mode === "fixed" && cents != null ? { mode: "fixed", cents } : { mode: "none" };
  const s = ctx.settings;
  const own = periods.filter((p) => p.deal_id === deal.id);
  if (!own.length) return null;
  const occ = dealOccurrences({
    today: ctx.today, from: ctx.today.slice(0, 7), to: ctx.today.slice(0, 7), currency: deal.currency, orgCurrency: s?.currency ?? deal.currency,
    agencyCommissionPercent: (s?.default_commission_bp ?? 0) / 100,
    talentCommissionPercent: ctx.talent ? { [ctx.talent.id]: ctx.talent.commission_bp == null ? null : ctx.talent.commission_bp / 100 } : {},
    agencyFee: fee(s?.agency_fee_mode, s?.agency_fee_bp, s?.agency_fee_cents),
    deals: [overviewDealFromRow(deal as unknown as DealRowForOverview, {
      fee: ctx.terms ? fee(ctx.terms.agency_fee_mode, ctx.terms.agency_fee_bp, ctx.terms.agency_fee_cents) : null, terms: ctx.terms ?? null,
      brandRoute: routeOf(deal, s), orgRoute: s?.invoice_route ?? null, revenueStart: s?.revenue_start ?? null, signed: ctx.unterschrieben,
    })],
    periods: own.map((p) => overviewPeriodFromRow(p as unknown as Parameters<typeof overviewPeriodFromRow>[0])),
    extras: items.filter((i) => i.deal_id === deal.id).map((e) => ({ dealId: e.deal_id, periodId: e.period_id, amountCents: e.amount_cents, commissionable: e.commissionable,
      createdAt: (e as ItemRow & { created_at?: string | null }).created_at ?? null })),
    invoices: invoices.filter((i) => i.deal_id === deal.id).map((r) => overviewInvoiceFromRow(r as unknown as InvoiceRowForOverview)),
  }).filter((o) => o.dealId === deal.id && !Number.isNaN(o.grossCents));
  // Umsatzbeteiligung ohne Honorar: Umsatz ist die Provision der Agentur, wie in den Kennzahlen (K-161)
  const wert = (o: (typeof occ)[number]) => (deal.model === "commission_only" ? o.managementCents : o.grossCents);
  const sum = (list: typeof occ) => list.reduce((a, o) => a + wert(o), 0);
  return { bookedCents: sum(occ.filter((o) => o.booked)), plannedCents: sum(occ.filter((o) => !o.booked)) };
}

/** Honorar netto: Monatsbeträge plus Zusatzpositionen; reine Provision und Event/Gifting ohne Honorar; stornierte Monate nicht (Befund 1). */
export function honorarCents(deal: DealFull, periods: readonly PeriodFull[], items: readonly ItemRow[]): number | null {
  if (deal.model === "commission_only" || NO_MONEY.has(deal.model)) return null;
  const own = periods.filter((p) => p.deal_id === deal.id);
  const storniert = new Set(own.filter(monatStorniert).map((p) => p.id));
  const lebend = own.filter((p) => !storniert.has(p.id));
  const base = own.length ? lebend.reduce((a, p) => a + (p.amount_cents ?? 0), 0) : (deal.budget_cents ?? 0);
  // Positionen ohne Monat gehören zum ersten Monat: sind alle Monate storniert, entfallen sie mit
  const alleStorniert = own.length > 0 && lebend.length === 0;
  return base + items.filter((i) => i.deal_id === deal.id && !(i.period_id && storniert.has(i.period_id)) && !(alleStorniert && !i.period_id)).reduce((a, i) => a + i.amount_cents, 0);
}

/** Auslagen-Verdacht (KZ-04): provisionspflichtige Positionen, deren Text nach Auslage aussieht. */
export function auslagenVerdacht(positions: readonly { description: string; commissionable: boolean }[], keywords: readonly string[]): number[] {
  const lines: SplitLine[] = positions.map((p) => ({ description: p.description, quantity: 1, unitPriceCents: 1, commissionFree: !p.commissionable }));
  return detectExpenseLikeLines(lines, keywords).map((f) => Number(f.params?.line ?? 0) - 1).filter((i) => i >= 0);
}

// ── Zeilen für Liste, Board und Ansicht nach Marke ─────────────────────────

export interface KoopZeile {
  deal: DealFull;
  talent: TalentRow | undefined;
  brand: BrandRow | undefined;
  talentName: string;
  brandName: string;
  modell: string;
  monate: MonatInfo[];
  ohneMonat: InvoiceEntry[];
  fokus: MonatInfo | undefined;
  status: Praesentation;
  arbeitsstand: string;
  frist: Frist | null;
  honorarCents: number | null;
  agenturCents: number | null;
  /** Vermittlerprovision gesamt (Aufwand der Agentur, Befund 4); 0 ohne Vermittler */
  vermittlerCents: number;
  vertraege: ContractLite[];
  terms: TermsRow | undefined;
  items: ItemRow[];
  archiviert: boolean;
  suchtext: string;
  kampagne: CampaignRow | undefined;
  /** Anzeige des Arbeitsstands (mit „Content freigegeben“) */
  arbeitsstandLabel: string;
}

export function baueZeilen(d: KoopDaten, opts: { today: string; timeZone: string; selbst?: boolean }): KoopZeile[] {
  const talents = new Map(d.talents.map((t) => [t.id, t]));
  const brands = new Map(d.brands.map((b) => [b.id, b]));
  const terms = new Map(d.terms.map((t) => [t.deal_id, t]));
  const campaigns = new Map((d.campaigns ?? []).map((c) => [c.id, c]));
  const labels = labelMap(d.labels);
  const periodsByDeal = groupBy(d.periods, (p) => p.deal_id);
  const itemsByDeal = groupBy(d.items, (i) => i.deal_id);
  // Sammelrechnungen (ohne Kooperation) hängen über ihre Monate an jeder beteiligten Kooperation
  const dealOfPeriod = new Map(d.periods.map((p) => [p.id, p.deal_id]));
  const invById = new Map(d.invoices.map((i) => [i.id, i]));
  const invByDeal = groupBy(d.invoices.filter((i) => i.deal_id), (i) => i.deal_id!);
  for (const l of d.invoicePeriods ?? []) {
    const dealId = dealOfPeriod.get(l.period_id);
    const inv = invById.get(l.invoice_id);
    if (!dealId || !inv || inv.deal_id === dealId) continue;
    const list = invByDeal.get(dealId) ?? [];
    if (!list.includes(inv)) invByDeal.set(dealId, [...list, inv]);
  }
  const contractById = new Map(d.contracts.map((c) => [c.id, c]));
  const contractsByDeal = groupBy<ContractLite>(d.contracts.filter((c) => c.deal_id).map((c) => ({ ...c, link: "main" as const })), (c) => c.deal_id!);
  for (const l of d.contractLinks ?? []) {
    const c = contractById.get(l.contract_id);
    if (c) contractsByDeal.set(l.deal_id, [...(contractsByDeal.get(l.deal_id) ?? []), { ...c, link: "link" as const }]);
  }
  return d.deals.map((deal) => {
    const periods = periodsByDeal.get(deal.id) ?? [];
    const items = itemsByDeal.get(deal.id) ?? [];
    const { monate, ohneMonat } = monateDerKooperation(deal, periods, invByDeal.get(deal.id) ?? [], opts.timeZone, d.invoicePeriods ?? [], terms.get(deal.id), !!opts.selbst);
    const talent = talents.get(deal.talent_id);
    const brand = brands.get(deal.brand_id);
    const fokus = fokusMonat(monate, opts.today);
    const status = praesentation(deal, monate, labels, fokus);
    const t = terms.get(deal.id);
    const split = aufteilung(deal, periods, items, t, talent, d.settings);
    const talentName = nameOfTalent(talent);
    const brandName = brand?.name ?? "–";
    const vertraege = contractsByDeal.get(deal.id) ?? [];
    const kampagne = deal.campaign_id ? campaigns.get(deal.campaign_id) : undefined;
    const stand = arbeitsstand(deal, fokus);
    return {
      deal, talent, brand, talentName, brandName,
      modell: MODEL_LABEL[deal.model] ?? deal.model,
      monate, ohneMonat, fokus, status,
      arbeitsstand: stand,
      arbeitsstandLabel: statusDef(stand, labels).label,
      frist: naechsteFrist(deal, monate, opts.today, { tage: d.settings?.requested_urgent_days, timeZone: opts.timeZone }),
      honorarCents: honorarCents(deal, periods, items),
      agenturCents: split.gesamt ? split.gesamt.managementNet : null,
      vermittlerCents: split.vermittler?.gesamtCents ?? 0,
      vertraege, terms: t, items, kampagne,
      archiviert: !!deal.archived_at,
      suchtext: [talentName, brandName, brand?.legal_name, deal.title, deal.deliverables, status.pill.label, statusDef(deal.status, labels).label,
        MODEL_LABEL[deal.model], dealCode(deal, d.settings?.invoice_number_prefix), deal.billing_legal_name, kampagne?.title, deal.exclusive_scope]
        .filter(Boolean).join(" ").toLowerCase(),
    };
  });
}

function groupBy<T>(list: readonly T[], key: (x: T) => string): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const x of list) {
    const k = key(x);
    const l = m.get(k);
    if (l) l.push(x);
    else m.set(k, [x]);
  }
  return m;
}

// ── Filter, Zähler, Sortierung (AD-041) ─────────────────────────────────────

export type KoopFilter = "aktiv" | Bucket | "alle" | "archiv";
export const FILTERS: { key: KoopFilter; label: string }[] = [
  { key: "aktiv", get label() { return t("Aktiv", "Active"); } },
  { key: "todo", get label() { return BUCKET_LABEL.todo; } },
  { key: "in_progress", get label() { return BUCKET_LABEL.in_progress; } },
  { key: "billing", get label() { return BUCKET_LABEL.billing; } },
  { key: "done", get label() { return BUCKET_LABEL.done; } },
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "archiv", get label() { return t("Archiv", "Archive"); } },
];

/**
 * Auswahl „Nach Creator/Marke filtern“ für Liste und Board aus derselben Regel (SICHT-AGENTUR-GELD-24): nur Creator und
 * Marken, die in den Kooperationen vorkommen (Befund 28), dazu die gerade gewählte; archivierte Creator mit Zusatz.
 */
export function creatorFilterOptionen(talents: readonly Pick<TalentRow, "id" | "display_name" | "first_name" | "last_name" | "status">[], zeilen: readonly Pick<KoopZeile, "deal">[], gewaehlt: string): { id: string; label: string }[] {
  const ids = new Set(zeilen.map((z) => z.deal.talent_id));
  return talents.filter((tl) => ids.has(tl.id) || tl.id === gewaehlt)
    .map((tl) => ({ id: tl.id, label: `${nameOfTalent(tl)}${tl.status === "archived" ? t(" (archiviert)", " (archived)") : ""}` }));
}
export function markenFilterOptionen(brands: readonly { id: string; name: string }[], zeilen: readonly Pick<KoopZeile, "deal">[], gewaehlt: string): { id: string; label: string }[] {
  const ids = new Set(zeilen.map((z) => z.deal.brand_id));
  return brands.filter((b) => ids.has(b.id) || b.id === gewaehlt).map((b) => ({ id: b.id, label: b.name }));
}

export function passtFilter(z: KoopZeile, f: KoopFilter): boolean {
  if (f === "archiv") return z.archiviert;
  if (z.archiviert) return false;
  if (f === "alle") return true;
  if (f === "aktiv") return !z.status.erledigt;
  if (f === "done") return z.status.erledigt;
  return !z.status.erledigt && z.status.bucket === f;
}

/** Zähler und Filter rechnen mit derselben Menge (fs209). */
export function zaehleFilter(zeilen: readonly KoopZeile[]): Record<KoopFilter, number> {
  const out = { aktiv: 0, todo: 0, in_progress: 0, billing: 0, done: 0, alle: 0, archiv: 0 } as Record<KoopFilter, number>;
  for (const z of zeilen) for (const f of FILTERS) if (passtFilter(z, f.key)) out[f.key] += 1;
  return out;
}

export function passtSuche(z: KoopZeile, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return q.split(/\s+/).every((part) => z.suchtext.includes(part));
}

// ── Board und Ansicht nach Marke ────────────────────────────────────────────

export interface BoardSpalte {
  key: string;
  pill: Pill;
  zeilen: KoopZeile[];
}

export function boardSpalten(zeilen: readonly KoopZeile[]): BoardSpalte[] {
  const map = new Map<string, KoopZeile[]>();
  for (const z of zeilen) map.set(z.status.key, [...(map.get(z.status.key) ?? []), z]);
  const order = (k: string) => {
    const i = BOARD_ORDER.indexOf(k);
    return i === -1 ? BOARD_ORDER.length : i;
  };
  return [...map.entries()]
    .sort((a, b) => order(a[0]) - order(b[0]))
    .map(([key, list]) => ({
      key,
      pill: list[0]!.status.pill,
      zeilen: [...list].sort((a, b) => (a.frist?.date ?? "9999").localeCompare(b.frist?.date ?? "9999") || a.talentName.localeCompare(b.talentName, "de")),
    }));
}

export interface MarkenGruppe {
  brandId: string;
  name: string;
  brand: BrandRow | undefined;
  zeilen: KoopZeile[];
}

export function nachMarke(zeilen: readonly KoopZeile[]): MarkenGruppe[] {
  const map = new Map<string, MarkenGruppe>();
  for (const z of zeilen) {
    const g = map.get(z.deal.brand_id) ?? { brandId: z.deal.brand_id, name: z.brandName, brand: z.brand, zeilen: [] };
    g.zeilen.push(z);
    map.set(z.deal.brand_id, g);
  }
  return [...map.values()]
    .map((g) => ({ ...g, zeilen: [...g.zeilen].sort((a, b) => a.status.rank - b.status.rank || b.deal.created_at.localeCompare(a.deal.created_at)) }))
    .sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** Hauptvertrag einer Kooperation: unterschriebener zuerst, dann teilweise signiert, versendet, Entwurf; aufgehobene zuletzt. */
export function sortiereVertraege(list: readonly ContractLite[]): ContractLite[] {
  const rank: Record<ContractLite["status"], number> = { signed: 0, partially_signed: 1, sent: 2, draft: 3, void: 4 };
  return [...list].sort((a, b) => rank[a.status] - rank[b.status] || b.created_at.localeCompare(a.created_at));
}

/** Rohe Links in Notizen werden kurze Verweise (Hub im bisherigen Portal). */
export function notizTeile(text: string): { text: string; href?: string }[] {
  const out: { text: string; href?: string }[] = [];
  const re = /https?:\/\/[^\s<]+/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: text.slice(last, i) });
    out.push({ text: /notion\./i.test(m[0]) ? t("Notion öffnen ↗", "Open Notion ↗") : t("Link öffnen ↗", "Open link ↗"), href: m[0] });
    last = i + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

// ── Verlauf (aus Zeitpunkten der Datensätze, ohne eigenes Protokoll) ─────────

export interface Ereignis {
  at: string;
  text: string;
  wer: string | null;
}

/** Felder, deren Änderung der Verlauf als „bearbeitet“ nennt (Protokoll, Befund 27) */
const FELD: Record<string, string> = {
  get title() { return t("Titel", "Title"); }, talent_id: "Creator", get brand_id() { return t("Marke", "Brand"); }, get model() { return t("Vergütungsmodell", "Remuneration model"); }, currency: "Währung", get budget_cents() { return t("Honorar", "Fee"); }, get commission_bp() { return t("Provision", "Commission"); },
  get start_month() { return t("Beginn", "Start"); }, get months() { return t("Laufzeit", "term"); }, get deliverables() { return t("Leistungsumfang", "Scope of services"); }, get talent_note() { return t("Notiz für den Creator", "Note for the creator"); }, get visible_to_talent() { return t("Sichtbarkeit im Creator-Portal", "Visibility in the creator portal"); },
  get platforms() { return t("Plattformen", "Platforms"); }, get usage_rights() { return t("Nutzungsrechte", "Usage rights"); }, get usage_rights_until() { return t("Nutzungsrechte bis", "Usage rights until"); }, get link() { return t("Link", "Link"); }, get content_deadline() { return t("Content-Deadline", "Content deadline"); },
  get products_flat_taxed() { return t("Pauschalversteuerung", "Flat-rate taxation"); }, get invoice_route() { return t("Abrechnungsweg", "Invoicing route"); }, get exclusive() { return t("Exklusivität", "Exclusivity"); }, get exclusive_until() { return t("Exklusivität bis", "Exclusivity until"); },
  get exclusive_scope() { return t("Exklusivität für", "Exclusivity for"); }, get campaign_id() { return t("Kampagne", "Campaign"); }, get free_revisions() { return t("Korrekturschleifen", "Revision rounds"); }, get goods_description() { return t("Produkte", "Products"); },
  get billing_legal_name() { return t("Rechnungsdaten", "Invoice details"); }, get billing_street() { return t("Rechnungsdaten", "Invoice details"); }, get billing_postal_code() { return t("Rechnungsdaten", "Invoice details"); }, get billing_city() { return t("Rechnungsdaten", "Invoice details"); },
  get billing_country() { return t("Rechnungsdaten", "Invoice details"); }, get billing_vat_id() { return t("Rechnungsdaten", "Invoice details"); }, get billing_register() { return t("Rechnungsdaten", "Invoice details"); }, get billing_email() { return t("Rechnungsdaten", "Invoice details"); },
  get billing_reference() { return t("Rechnungsdaten", "Invoice details"); }, get billing_payment_terms_days() { return t("Rechnungsdaten", "Invoice details"); },
};
const MONATSFELD: Record<string, string> = {
  get amount_cents() { return t("Betrag", "Amount"); }, get commission_forecast_cents() { return t("erwartete Provision", "expected commission"); }, get content_deadline() { return t("Content-Deadline", "Content deadline"); }, get goods_value_cents() { return t("Warenwert", "Value of goods"); }, get note() { return t("Notiz", "Note"); },
};

/** Ereignisse aus dem Protokoll: Statuswechsel, Monatsstände, Content-Freigaben, Verknüpfungen und Bearbeitungen. */
export function verlaufAusProtokoll(history: readonly HistoryRow[], input: {
  periods: readonly PeriodFull[];
  submissions?: readonly SubmissionRow[];
  labels?: Labels;
  memberName: (userId: string | null | undefined) => string | null;
  talentName: string;
  /** Anlage der Kooperation: Monate, die mit ihr entstehen, erscheinen nicht einzeln */
  createdAt?: string;
  /** Einmal-Modell: Stand der Kooperation und ihres Monats ändern sich gemeinsam, der Verlauf nennt ihn einmal (Befund 22) */
  einmal?: boolean;
}): Ereignis[] {
  const anlage = input.createdAt ? Date.parse(input.createdAt) : NaN;
  const out: Ereignis[] = [];
  const monthOf = (id: string | null, changes: Record<string, unknown>) => {
    const p = input.periods.find((x) => x.id === id);
    const raw = p?.month ?? (typeof changes.month === "string" ? changes.month : Array.isArray(changes.month) ? String(changes.month[1] ?? changes.month[0] ?? "") : "");
    return raw ? monthName(raw.slice(0, 7)) : t("einen Monat", "a month");
  };
  const pair = (v: unknown): [unknown, unknown] => (Array.isArray(v) ? [v[0], v[1]] : [undefined, v]);
  const st = (k: unknown) => (typeof k === "string" ? statusDef(k, input.labels).label : "–");
  const eigene = new Set((input.submissions ?? []).map((s) => s.id));
  for (const h of history) {
    // Systemläufe (Einrichtung, Abgleich) sind keine Bearbeitung durch jemanden
    if (h.actor_kind === "system") continue;
    const who = h.actor_kind === "talent" ? input.talentName : input.memberName(h.actor);
    const c = h.changes ?? {};
    const push = (text: string) => out.push({ at: h.at, text, wer: who });
    if (h.table_name === "deals" && h.action === "update") {
      if ("status" in c) {
        const [a, b] = pair(c.status);
        push(`Status: ${st(a)} → ${st(b)}`);
      }
      const felder = [...new Set(Object.keys(c).map((k) => FELD[k]).filter((x): x is string => !!x))];
      if (felder.length) push(t(`Bearbeitet: ${felder.join(", ")}`, `Edited: ${felder.join(", ")}`));
    } else if (h.table_name === "deals" && h.action === "deal_cancel") {
      push(t(`Kooperation storniert${typeof c.fee_percent === "number" && c.fee_percent > 0 ? t(` mit Ausfallhonorar ${prozent(c.fee_percent)}`, ` with cancellation fee ${prozent(c.fee_percent)}`) : ""}${typeof c.reason === "string" ? `: ${c.reason}` : ""}`, `Deal cancelled${typeof c.fee_percent === "number" && c.fee_percent > 0 ? t(` mit Ausfallhonorar ${prozent(c.fee_percent)}`, ` with cancellation fee ${prozent(c.fee_percent)}`) : ""}${typeof c.reason === "string" ? `: ${c.reason}` : ""}`));
    } else if (h.table_name === "deals" && h.action === "follow_up") {
      push(t(`Nachkauf für ${typeof c.month === "string" ? monthName(c.month) : t("einen Monat", "a month")}: ${typeof c.description === "string" ? c.description : t("Position", "Item")}`, `Follow-up purchase for ${typeof c.month === "string" ? monthName(c.month) : t("einen Monat", "a month")}: ${typeof c.description === "string" ? c.description : t("Position", "Item")}`));
    } else if (h.table_name === "deals" && (h.action === "bonus_add" || h.action === "bonus_met" || h.action === "bonus_missed")) {
      const was = typeof c.description === "string" ? c.description : "Bonus";
      push(h.action === "bonus_add" ? t(`Bonus vorbereitet: ${was}`, `Bonus prepared: ${was}`) : h.action === "bonus_met" ? t(`Bonus erfüllt: ${was}`, `Bonus achieved: ${was}`) : t(`Bonus nicht erreicht: ${was}`, `Bonus not reached: ${was}`));
    } else if (h.table_name === "deal_periods") {
      const m = monthOf(h.row_id, c);
      if (h.action === "period_cancel") push(t(`${m} storniert${typeof c.fee_cents === "number" && c.fee_cents > 0 ? t(" mit Ausfallhonorar", " with cancellation fee") : ""}`, `${m} cancelled${typeof c.fee_cents === "number" && c.fee_cents > 0 ? t(" mit Ausfallhonorar", " with cancellation fee") : ""}`));
      else if (h.action === "period_outcome") {
        const art = typeof c.outcome === "string" && c.outcome in MONTH_OUTCOME_LABEL ? MONTH_OUTCOME_LABEL[c.outcome as keyof typeof MONTH_OUTCOME_LABEL] : t("Ausgang", "Outcome");
        push(`${m}: ${art}${typeof c.reason === "string" ? ` · ${c.reason}` : ""}`);
      }
      else if (h.action === "period_reopen") push(t(`${m} wieder geöffnet`, `${m} reopened`));
      else if (h.action === "period_move") push(t(`Leistungsmonat verschoben auf ${m}`, `Service month moved to ${m}`));
      // Monate, die mit der Kooperation (oder vom System) angelegt wurden, stecken in „Kooperation angelegt“
      else if (h.action === "insert" && h.actor_kind !== "system" && !(Math.abs(Date.parse(h.at) - anlage) < 5 * 60_000)) push(`${m} angelegt`);
      else if (h.action === "delete") push(`${m} entfernt`);
      else if (h.action === "update") {
        if ("manual_status" in c && !input.einmal) {
          const [a, b] = pair(c.manual_status);
          if (!("content_approved_at" in c)) push(t(`Leistungsstand ${m}: ${st(a)} → ${st(b)}`, `Service status ${m}: ${st(a)} → ${st(b)}`));
        }
        // Die Veröffentlichung selbst steht mit ihrem Datum im Verlauf; hier nur das Zurücknehmen
        if ("published_on" in c && !pair(c.published_on)[1]) push(t(`Veröffentlichung ${m} zurückgenommen`, `Publication ${m} withdrawn`));
        const felder = [...new Set(Object.keys(c).map((k) => MONATSFELD[k]).filter((x): x is string => !!x))];
        if (felder.length) push(`${m} bearbeitet: ${felder.join(", ")}`);
      }
    } else if (h.table_name === "deal_content_submissions" && !eigene.has(h.row_id ?? "")) {
      // Einreichungen, die es noch gibt, stehen mit ihren eigenen Zeitpunkten im Verlauf (verlauf)
      const sub = input.submissions?.find((s) => s.id === h.row_id);
      const m = monthOf(sub?.period_id ?? null, c);
      const version = typeof c.version === "number" ? c.version : sub?.version;
      if (h.action === "insert") push(t(`Content für ${m} eingereicht${version ? t(` (Version ${version})`, ` (version ${version})`) : ""}`, `Content for ${m} submitted${version ? t(` (Version ${version})`, ` (version ${version})`) : ""}`));
      else if (h.action === "update" && "status" in c) {
        const b = pair(c.status)[1];
        if (b === "approved") push(t(`Content für ${m} freigegeben${version ? t(` (Version ${version})`, ` (version ${version})`) : ""}`, `Content for ${m} approved${version ? t(` (Version ${version})`, ` (version ${version})`) : ""}`));
        if (b === "changes_requested") {
          const r = pair(c.revision_round)[1];
          push(t(`Korrektur für ${m} angefordert${typeof r === "number" ? t(` (Korrekturschleife ${r})`, ` (revision round ${r})`) : ""}`, `Correction for ${m} requested${typeof r === "number" ? t(` (Korrekturschleife ${r})`, ` (revision round ${r})`) : ""}`));
        }
      }
    } else if (h.table_name === "contract_deal_links") {
      push(h.action === "delete" ? t("Vertragsverknüpfung gelöst", "Contract link removed") : t("Vertrag verknüpft (weitere Kooperation)", "Contract linked (additional deal)"));
    }
  }
  return out;
}

export function verlauf(input: {
  deal: DealFull;
  monate: readonly MonatInfo[];
  ohneMonat: readonly InvoiceEntry[];
  contracts: readonly ContractLite[];
  messages: readonly MessageLite[];
  talentName: string;
  memberName: (userId: string | null | undefined) => string | null;
  history?: readonly HistoryRow[];
  submissions?: readonly SubmissionRow[];
  labels?: Labels;
}): Ereignis[] {
  const out: Ereignis[] = input.history?.length
    ? verlaufAusProtokoll(input.history, { periods: input.monate.map((m) => m.period), submissions: input.submissions, labels: input.labels, memberName: input.memberName,
        talentName: input.talentName, createdAt: input.deal.created_at, einmal: !MONTH_BASED.has(input.deal.model) })
    : [];
  const { deal } = input;
  out.push({ at: deal.created_at, text: t("Kooperation angelegt", "Deal created"), wer: input.memberName(deal.created_by) });
  if (deal.archived_at) out.push({ at: deal.archived_at, text: t("Kooperation archiviert", "Deal archived"), wer: null });
  // Eine Sammelrechnung hängt an mehreren Monaten: jede Rechnung nur einmal (Befund 22)
  const invoices = [...input.monate.flatMap((m) => m.invoices), ...input.ohneMonat].filter((e, i, list) => list.findIndex((x) => x.row.id === e.row.id) === i);
  // Wer hat veröffentlicht? Aus dem Protokoll des Monats (period_set_published)
  const veroeffentlichtVon = (periodId: string): string | null => {
    const h = (input.history ?? []).find((x) => x.table_name === "deal_periods" && x.row_id === periodId && x.action === "update" && "published_on" in (x.changes ?? {})
      && !!(Array.isArray(x.changes.published_on) ? x.changes.published_on[1] : x.changes.published_on));
    return h ? (h.actor_kind === "talent" ? input.talentName : input.memberName(h.actor)) : null;
  };
  // Content-Einreichungen mit ihren eigenen Zeitpunkten (Einreichung, Prüfung)
  for (const s of input.submissions ?? []) {
    const m = input.monate.find((x) => x.period.id === s.period_id);
    const monat = m ? monthName(m.month) : t("einen Monat", "a month");
    out.push({ at: s.submitted_at, text: t(`Content für ${monat} eingereicht (Version ${s.version})`, `Content for ${monat} submitted (version ${s.version})`), wer: input.memberName(s.recorded_by) });
    if (s.reviewed_at && s.status === "approved") out.push({ at: s.reviewed_at, text: t(`Content für ${monat} freigegeben (Version ${s.version})`, `Content for ${monat} approved (version ${s.version})`), wer: input.memberName(s.reviewed_by) });
    if (s.reviewed_at && s.status === "changes_requested") {
      out.push({ at: s.reviewed_at, text: t(`Korrektur für ${monat} angefordert${s.revision_round ? t(` (Korrekturschleife ${s.revision_round})`, ` (revision round ${s.revision_round})`) : ""}`, `Correction for ${monat} requested${s.revision_round ? t(` (Korrekturschleife ${s.revision_round})`, ` (revision round ${s.revision_round})`) : ""}`), wer: input.memberName(s.reviewed_by) });
    }
  }
  for (const m of input.monate) {
    const p = m.period;
    if (p.invoice_requested_at) out.push({ at: p.invoice_requested_at, text: t(`Rechnung für ${monthName(m.month)} beim Creator angefordert`, `Invoice for ${monthName(m.month)} requested from the creator`), wer: input.memberName(p.invoice_requested_by) });
    if (p.published_on) out.push({ at: `${p.published_on}T12:00:00Z`, text: t(`${monthName(m.month)} veröffentlicht${p.post_url ? t(" (Beitrag verlinkt)", " (post linked)") : ""}`, `${monthName(m.month)} published${p.post_url ? t(" (Beitrag verlinkt)", " (post linked)") : ""}`), wer: veroeffentlichtVon(p.id) });
    // Übernommene Monate bucht die Übernahme, nicht jemand von Hand (PRAXIS-R3-8)
    if (p.booked_at && p.imported_state) out.push({ at: p.booked_at, text: p.imported_state === "paid" ? t(`${monthName(m.month)} als bezahlt aus dem bisherigen System übernommen`, `${monthName(m.month)} taken over from the previous system as paid`) : t(`${monthName(m.month)} als „Marke bezahlt“ aus dem bisherigen System übernommen`, `${monthName(m.month)} taken over from the previous system as “brand paid”`), wer: input.memberName(p.booked_by) });
    else if (p.booked_at) out.push({ at: p.booked_at, text: t(`${monthName(m.month)} von Hand als bezahlt abgehakt`, `${monthName(m.month)} marked as paid by hand`), wer: input.memberName(p.booked_by) });
  }
  for (const { row } of invoices) {
    const nr = row.number ? t(`Rechnung ${row.number}`, `Invoice ${row.number}`) : t("Rechnung ohne Nummer", "Invoice without number");
    const toBrand = isBrandInvoice(row);
    // Aus dem bisherigen System übernommen: niemand hat sie im Portal eingereicht oder freigegeben (PRAXIS-R3-8)
    const imp = row as { imported?: boolean | null; imported_at?: string | null; imported_by?: string | null };
    if (imp.imported) {
      out.push({ at: imp.imported_at ?? row.created_at, text: t(`${nr} aus dem bisherigen System übernommen`, `${nr} taken over from the previous system`), wer: input.memberName(imp.imported_by ?? null) });
      if (row.paid_at && imp.imported_at && row.paid_at > imp.imported_at) out.push({ at: row.paid_at, text: toBrand ? t(`Zahlung zu ${nr} bestätigt`, `Payment for ${nr} confirmed`) : t(`${nr} ausgezahlt`, `${nr} paid out`), wer: input.memberName(row.paid_confirmed_by as string | null) });
      continue;
    }
    if (row.submitted_at && !toBrand) out.push({ at: row.submitted_at, text: t(`${nr} eingereicht`, `${nr} submitted`), wer: input.talentName });
    if (row.approved_at) out.push({ at: row.approved_at, text: t(`${nr} freigegeben`, `${nr} approved`), wer: input.memberName(row.approved_by as string | null) });
    if (row.sent_at && toBrand) out.push({ at: row.sent_at, text: t(`${nr} an die Marke versendet`, `${nr} sent to the brand`), wer: null });
    if (row.paid_at) out.push({ at: row.paid_at, text: toBrand ? t(`Zahlung zu ${nr} bestätigt`, `Payment for ${nr} confirmed`) : t(`${nr} ausgezahlt`, `${nr} paid out`), wer: input.memberName(row.paid_confirmed_by as string | null) });
    if (row.status === "revision_requested" && typeof row.reviewed_at === "string") out.push({ at: row.reviewed_at, text: t(`Korrektur zu ${nr} angefordert`, `Correction for ${nr} requested`), wer: input.memberName(row.reviewed_by as string | null) });
  }
  for (const c of input.contracts) {
    if (c.sent_at) out.push({ at: c.sent_at, text: t(`Vertrag „${c.title}“ versendet`, `Contract “${c.title}” sent`), wer: null });
    if (c.completed_at) out.push({ at: c.completed_at, text: t(`Vertrag „${c.title}“ beidseitig signiert`, `Contract “${c.title}” signed by both parties`), wer: null });
  }
  for (const msg of input.messages) {
    const body = msg.body.trim();
    const short = body.length > 70 ? `${body.slice(0, 70)} …` : body;
    out.push({
      at: msg.created_at,
      text: t(`Nachricht: ${short || t("Datei", "File")}`, `Message: ${short || t("Datei", "File")}`),
      wer: msg.sender_side === "talent" ? input.talentName : input.memberName(msg.sender_user_id),
    });
  }
  return out.filter((e) => !!e.at).sort((a, b) => b.at.localeCompare(a.at));
}

// ── Nächster Schritt ────────────────────────────────────────────────────────

export type SchrittAktion =
  | { kind: "link"; label: string; to: string }
  | { kind: "content"; label: string }
  | { kind: "anfordern"; label: string }
  | { kind: "nachricht"; label: string }
  | { kind: "wiederherstellen"; label: string };

export interface Schritt {
  text: string;
  frist: string | null;
  aktion: SchrittAktion | null;
}

const OPEN_STAGE_TEXT: Partial<Record<DealStatus, string>> = {
  get planning() { return t("Leistungen und Fristen abstimmen.", "Agree on services and deadlines."); },
  get concept() { return t("Konzept des Creators abwarten und freigeben.", "Wait for the creator's concept and approve it."); },
  get production() { return t("Content ist in Produktion.", "Content is in production."); },
  get published() { return t("Content ist veröffentlicht. Reporting und Rechnung vorbereiten.", "Content is published. Prepare reporting and invoice."); },
  get reporting() { return t("Reporting abschließen und die Rechnung anfordern.", "Complete reporting and request the invoice."); },
};

/**
 * Was als Nächstes zu tun ist, für den gewählten Monat. Rechnungsschritte kommen aus der Fachlogik des Rechnungsbereichs
 * (nächste Aktion je Rechnung), der Rest aus Arbeitsstand, Anforderung und Vertrag.
 */
export function naechsterSchritt(input: {
  deal: DealFull;
  status: Praesentation;
  monat: MonatInfo | undefined;
  vertraege: readonly ContractLite[];
  kannAnfordern: boolean;
  today: string;
  /** Nächste Rechnungsaktion (aus model/invoice-view): Beschriftung und Ziel */
  rechnung: { label: string; to: string } | null;
  /** Frist der Creator-Rechnung: Tage nach der Anforderung und Zone der Agentur */
  frist?: { tage?: number | null; timeZone?: string };
  /** Gutschriftverfahren mit dem Creator vereinbart (Weg über die Agentur): die Agentur erstellt die Gutschrift selbst (GELD-23) */
  gutschrift?: boolean;
}): Schritt {
  const { deal, monat } = input;
  const call = (m: MonatInfo) => monthName(m.month);
  const gutschriftLink = (m: MonatInfo) => ({ kind: "link" as const, label: t("Abrechnung erstellen (Gutschrift)", "Create statement (self-billing invoice)"), to: `/agentur/rechnungen/neu?art=self_billing&kooperation=${deal.id}&monat=${m.month}` });
  if (deal.archived_at) return { text: t("Die Kooperation ist archiviert und im Creator-Portal nicht sichtbar.", "The deal is archived and not visible in the creator portal."), frist: null, aktion: { kind: "wiederherstellen", label: t("Wiederherstellen", "Restore") } };
  if (input.status.key === "collection") return { text: t("Eine Rechnung an die Marke liegt beim Inkasso.", "An invoice to the brand is with debt collection."), frist: null, aktion: input.rechnung ? { kind: "link", ...input.rechnung } : null };
  if (deal.status === "blocked") return { text: t("Die Kooperation ist blockiert. Grund mit Marke und Creator klären.", "The deal is blocked. Clarify the reason with brand and creator."), frist: null, aktion: { kind: "nachricht", label: t("Nachricht an den Creator", "Message to the creator") } };
  if (deal.status === "cancelled" && !input.status.hinweis) return { text: t("Die Kooperation ist storniert.", "The deal is cancelled."), frist: null, aktion: null };
  if (input.rechnung) return { text: `${input.rechnung.label}${monat ? ` · ${call(monat)}` : ""}.`, frist: null, aktion: { kind: "link", ...input.rechnung } };
  const stage = leistungsstand(deal, monat);
  const deadline = monat?.period.content_deadline ?? deal.content_deadline;
  if (monat?.period.invoice_requested_at && !monat.invoices.some((e) => isCreatorInvoice(e.row) && e.record && isLive(e.record) && e.record.reviewStatus !== "not_submitted")) {
    const since = tagIn(monat.period.invoice_requested_at, input.frist?.timeZone ?? "UTC");
    const bis = creatorFrist(monat.period, input.frist ?? {});
    // Gutschriftverfahren: eine frühere Anforderung kann der Creator nicht erfüllen; die Agentur erstellt die Gutschrift (GELD-23)
    if (input.gutschrift) return { text: t(`Abrechnung für ${call(monat)} erstellen: Die Agentur rechnet für den Creator ab (Gutschrift), er stellt keine eigene Rechnung.`, `Create the statement for ${call(monat)}: the agency bills on the creator's behalf (self-billing), so they do not issue their own invoice.`), frist: null, aktion: gutschriftLink(monat) };
    return { text: t(`Rechnung des Creators für ${call(monat)} ausstehend, angefordert am ${dmy(since)}.`, `Creator's invoice for ${call(monat)} pending, requested on ${dmy(since)}.`), frist: bis, aktion: { kind: "nachricht", label: t("Nachricht an den Creator", "Message to the creator") } };
  }
  if (istFreigegeben(monat)) {
    return { text: t(`Content für ${call(monat!)} ist freigegeben. Sobald der Beitrag online ist, Datum und Link eintragen; ohne Veröffentlichung (UGC) direkt die Abrechnung vorbereiten.`, `Content for ${call(monat!)} is approved. As soon as the post is online, enter date and link; without publication (UGC) prepare invoicing directly.`),
      frist: null, aktion: { kind: "content", label: t("Veröffentlichung eintragen", "Record publication") } };
  }
  if (deal.status === "contract" || stage === "contract") {
    const draft = input.vertraege.find((c) => c.status === "draft");
    const open = input.vertraege.find((c) => c.status === "sent" || c.status === "partially_signed");
    if (open) return { text: t("Vertrag ist zur Unterschrift versendet. Unterschriften abwarten.", "Contract has been sent for signature. Wait for the signatures."), frist: deadline, aktion: { kind: "link", label: t("Vertrag öffnen", "Open contract"), to: `/agentur/vertraege/${open.id}` } };
    if (draft) return { text: t("Vertrag zur Unterschrift vorbereiten und senden.", "Prepare and send the contract for signature."), frist: deadline, aktion: { kind: "link", label: t("Vertrag senden", "Send contract"), to: `/agentur/vertraege/${draft.id}/senden` } };
    return { text: t("Vertrag anlegen und mit dieser Kooperation verknüpfen.", "Create a contract and link it to this deal."), frist: deadline, aktion: { kind: "link", label: t("Vertrag erstellen", "Create contract"), to: `/agentur/vertraege/neu?kooperation=${deal.id}` } };
  }
  if (stage === "content_approval") return { text: t(`Content für ${monat ? call(monat) : t("diese Kooperation", "this deal")} prüfen${deadline ? ` bis ${dmy(deadline)}` : ""}.`, `Review content for ${monat ? call(monat) : t("diese Kooperation", "this deal")}${deadline ? ` by ${dmy(deadline)}` : ""}.`), frist: deadline, aktion: { kind: "content", label: t("Content prüfen", "Review content") } };
  // Praxistest K-138: Rechnung an die Marke storniert, keine neue gestellt. Gilt in jedem Arbeitsstand, auch wenn der Creator
  // seinen Anteil schon bekommen hat.
  if (monat && monat.status === "brand_invoice_cancelled" && !NO_MONEY.has(deal.model)) {
    const ausgezahlt = monat.settlement.creator === "paid";
    return {
      text: ausgezahlt
        ? t(`Die Rechnung an die Marke für ${call(monat)} ist storniert, der Creator-Anteil ist schon ausgezahlt. Neue Rechnung an die Marke erstellen oder die Auszahlung zurückfordern.`,
          `The invoice to the brand for ${call(monat)} is cancelled, the creator's share has already been paid out. Create a new invoice to the brand or reclaim the payout.`)
        : t(`Die Rechnung an die Marke für ${call(monat)} ist storniert. Neue Rechnung an die Marke erstellen.`, `The invoice to the brand for ${call(monat)} is cancelled. Create a new invoice to the brand.`),
      frist: null,
      aktion: { kind: "link", label: t("Neue Rechnung an die Marke", "New invoice to the brand"), to: `/agentur/rechnungen/neu?kooperation=${deal.id}&monat=${monat.month}` },
    };
  }
  // Über die Agentur: die Agentur stellt der Marke ihre Rechnung, sobald die Leistung erbracht ist (erster Abrechnungsschritt)
  if (monat && (stage === "published" || stage === "reporting" || stage === "invoicing") && monat.period.status !== "cancelled"
      && monat.settlement.kinds.customer === "agency_to_brand" && monat.settlement.customer === "none" && !NO_MONEY.has(deal.model)) {
    return { text: t(`Rechnung an die Marke für ${call(monat)} erstellen.`, `Create the invoice to the brand for ${call(monat)}.`), frist: null,
      aktion: { kind: "link", label: t("Rechnung an die Marke erstellen", "Create invoice to the brand"), to: `/agentur/rechnungen/neu?kooperation=${deal.id}&monat=${monat.month}` } };
  }
  if (monat && (stage === "published" || stage === "reporting" || stage === "invoicing") && input.kannAnfordern && !monat.period.invoice_requested_at && !monat.invoices.some((e) => isCreatorInvoice(e.row) && e.record && isLive(e.record) && e.record.reviewStatus !== "not_submitted")) {
    if (input.gutschrift) return { text: t(`Abrechnung für ${call(monat)} erstellen: Die Agentur rechnet für den Creator ab (Gutschrift).`, `Create the statement for ${call(monat)}: the agency bills on the creator's behalf (self-billing).`), frist: null, aktion: gutschriftLink(monat) };
    return { text: t(`Rechnung für ${call(monat)} beim Creator anfordern.`, `Request the invoice for ${call(monat)} from the creator.`), frist: null, aktion: { kind: "anfordern", label: t("Rechnung anfordern", "Request invoice") } };
  }
  // Eine Seite ist bezahlt, die andere fehlt noch (Marke zahlt vor der Creator-Rechnung, Provision offen)
  if (monat && monat.settlement.status !== "paid" && monat.settlement.status !== "cancelled") {
    const s = monat.settlement;
    if (s.customer === "paid" && s.creator === "invoiced" && s.kinds.creator === "agency_to_talent") {
      const prov = monat.invoices.find((e) => e.row.kind === "agency_to_talent" && e.row.status === "sent");
      return { text: t(`Die Provisionsrechnung für ${call(monat)} liegt beim Creator. Zahlungseingang abwarten und bestätigen.`, `The commission invoice for ${call(monat)} is with the creator. Wait for the payment and confirm it.`), frist: prov?.row.effective_due_date ?? null,
        aktion: prov ? { kind: "link", label: t("Rechnung öffnen", "Open invoice"), to: `/agentur/rechnungen/${prov.row.id}` } : null };
    }
    if (s.customer === "paid" && s.creator === "none" && s.kinds.creator === "talent_to_agency" && input.kannAnfordern) {
      if (input.gutschrift) return { text: t(`Die Marke hat für ${call(monat)} gezahlt. Jetzt die Gutschrift erstellen, damit ausgezahlt werden kann.`, `The brand has paid for ${call(monat)}. Now create the self-billing invoice so the payout can be made.`), frist: null, aktion: gutschriftLink(monat) };
      return { text: t(`Die Marke hat für ${call(monat)} gezahlt. Jetzt die Rechnung beim Creator anfordern, damit ausgezahlt werden kann.`, `The brand has paid for ${call(monat)}. Now request the invoice from the creator so the payout can be made.`), frist: null, aktion: { kind: "anfordern", label: t("Rechnung anfordern", "Request invoice") } };
    }
    if (s.customer === "paid" && s.creator === "none" && s.kinds.creator === "agency_to_talent") {
      return { text: t(`Die Marke hat den Creator für ${call(monat)} bezahlt. Jetzt die Provisionsrechnung an den Creator stellen.`, `The brand has paid the creator for ${call(monat)}. Now issue the commission invoice to the creator.`), frist: null,
        aktion: { kind: "link", label: t("Provisionsrechnung erstellen", "Create commission invoice"), to: `/agentur/rechnungen/neu?art=agency_to_talent&kooperation=${deal.id}&monat=${monat.month}` } };
    }
  }
  if (monat && monat.settlement.status === "paid" && monat.booking.booked) return { text: t("Alle Zahlungen sind eingegangen, der Monat ist erledigt.", "All payments have been received, the month is settled."), frist: null, aktion: { kind: "link", label: t("Rechnungen ansehen", "View invoices"), to: "/agentur/rechnungen" } };
  if (monat && monat.settlement.status === "paid") return { text: t("Alle Zahlungen sind eingegangen.", "All payments have been received."), frist: null, aktion: { kind: "link", label: t("Rechnungen ansehen", "View invoices"), to: "/agentur/rechnungen" } };
  const base = OPEN_STAGE_TEXT[stage as DealStatus] ?? (stage === "done" ? t("Kooperation abgeschlossen.", "Deal completed.") : t("Leistungen und Fristen abstimmen.", "Agree on services and deadlines."));
  const text = monat && stage === "planning"
    ? t(`Leistungen und Fristen für ${call(monat)} abstimmen.${deadline ? t(` Geplante Abgabe: ${dmy(deadline)}.`, ` Planned delivery: ${dmy(deadline)}.`) : ""}`, `Agree on services and deadlines for ${call(monat)}.${deadline ? t(` Geplante Abgabe: ${dmy(deadline)}.`, ` Planned delivery: ${dmy(deadline)}.`) : ""}`)
    : base;
  return { text, frist: deadline && stage !== "done" ? deadline : null, aktion: { kind: "nachricht", label: t("Nachricht an den Creator", "Message to the creator") } };
}

// ── Rechnung anfordern: Prüfung und Vorschläge (AD-046 … AD-048) ────────────

export interface PruefPunkt {
  /** Pflichtangaben der Fachlogik oder Leistungsprüfung der Kooperation (Befund 13) */
  code: RequestCheckCode | "LEISTUNG_OFFEN" | "MONAT_ZUKUNFT";
  text: string;
  wo: { label: string; to: string } | null;
}

const CHECK_TEXT: Record<RequestCheckCode, string> = {
  get RECIPIENT_LEGAL_NAME() { return t("Rechtlicher Name des Rechnungsempfängers fehlt.", "Legal name of the invoice recipient is missing."); },
  get RECIPIENT_ADDRESS() { return t("Anschrift des Rechnungsempfängers fehlt oder ist unvollständig.", "Address of the invoice recipient is missing or incomplete."); },
  get RECIPIENT_VAT_ID() { return t("USt-IdNr. des Rechnungsempfängers fehlt.", "VAT ID of the invoice recipient is missing."); },
  get ISSUER_TAX_ID() { return t("Steuernummer oder USt-IdNr. des Creators fehlt.", "Tax number or VAT ID of the creator is missing."); },
  get ISSUER_VAT_ID() { return t("USt-IdNr. des Creators fehlt.", "VAT ID of the creator is missing."); },
  get ISSUER_IBAN() { return t("IBAN des Creators fehlt.", "IBAN of the creator is missing."); },
  get ISSUER_ADDRESS() { return t("Anschrift des Creators fehlt.", "Address of the creator is missing."); },
  get ISSUER_ABROAD() { return t("Der Creator sitzt nicht im Land der Agentur. Steuerliche Behandlung prüfen.", "The creator is not based in the agency's country. Check the tax treatment."); },
  get AMOUNT() { return t("Für den Monat ist kein Betrag hinterlegt.", "No amount is on file for the month."); },
  get CURRENCY() { return t("Die Währung der Kooperation ist für die Agentur nicht freigeschaltet.", "The deal's currency is not enabled for the agency."); },
  get NOT_VISIBLE_IN_PORTAL() { return t("Die Kooperation ist im Creator-Portal nicht sichtbar.", "The deal is not visible in the creator portal."); },
  get NO_PORTAL_ACCOUNT() { return t("Der Creator hat noch kein Konto im Portal und sieht die Anforderung nur per E-Mail.", "The creator has no portal account yet and only sees the request by email."); },
};

export interface Empfaenger {
  name: string;
  zeilen: string[];
  country: string | null;
  vatId: string | null;
  hinweis: string | null;
  /** Wo die Angaben gepflegt werden */
  wo: { label: string; to: string };
}

/**
 * Wer bekommt die Rechnung des Creators? Abrechnung über die Agentur: die Agentur selbst. Direkt an die Marke: der
 * Rechnungsempfänger der Marke (Angaben der Kooperation vor der vertretenden Agentur vor der Marke, billingPartyOf).
 */
export function empfaengerDerCreatorRechnung(deal: DealFull, brand: BrandRow | undefined, settings: SettingsLite | null, orgName: string, brands: readonly BrandRow[] = []): Empfaenger {
  if (routeOf(deal, settings) !== "direct_to_brand") {
    return {
      name: settings?.legal_name?.trim() || orgName,
      zeilen: [settings?.street, [settings?.postal_code, settings?.city].filter(Boolean).join(" "), settings?.country].filter((x): x is string => !!x && !!x.trim()),
      country: settings?.country ?? null,
      vatId: settings?.vat_id ?? null,
      hinweis: t("Der Creator stellt die Rechnung an die Agentur.", "The creator invoices the agency."),
      wo: { label: t("Einstellungen · Firma", "Settings · Company"), to: "/agentur/einstellungen/firma" },
    };
  }
  const party = billingPartyOf(brand, brand ? [brand, ...brands] : brands, deal);
  return {
    name: party?.name ?? "–",
    zeilen: [party?.street, [party?.postalCode, party?.city].filter(Boolean).join(" "), party?.country].filter((x): x is string => !!x && !!x.trim()),
    country: party?.country ?? null,
    vatId: party?.vatId ?? null,
    hinweis: party?.onBehalfOf
      ? t(`Die Rechnung geht an die Agentur der Marke, für ${party.onBehalfOf}. Der Creator stellt direkt an die Marke; die Agentur stellt ihm ihre Provision.`, `The invoice goes to the brand's agency, for ${party.onBehalfOf}. The creator invoices the brand directly; the agency invoices its commission to the creator.`)
      : t("Der Creator stellt direkt an die Marke; die Agentur stellt ihm ihre Provision in Rechnung.", "The creator invoices the brand directly; the agency invoices its commission to the creator."),
    wo: party?.viaAgencyId
      ? { label: t("Marke · Agentur und Rechnung", "Brand · agency and invoice"), to: `/agentur/marken/${party.viaAgencyId}` }
      : { label: t("Kooperation bearbeiten · Rechnungsdaten", "Edit deal · invoice details"), to: `/agentur/kooperationen/${deal.id}/bearbeiten` },
  };
}

export function anforderungsPruefung(input: {
  deal: DealFull;
  period: PeriodFull;
  talent: (TalentRow & { street?: string | null; postal_code?: string | null; vat_id?: string | null; tax_number?: string | null }) | undefined;
  empfaenger: Empfaenger;
  settings: SettingsLite | null;
  iban: string | null | undefined;
  hasPortalAccount: boolean;
  /** Leistungsstand des Monats (Arbeitsstand) und heutiges Datum: Anforderung vor erbrachter Leistung ist „wichtig“ (Befund 13) */
  stand?: string | null;
  today?: string;
  /** Beiträge der Kooperation (0068): ein freigegebener oder live gemeldeter Beitrag zählt als erbrachter Content (Prüf-Chat CP-023) */
  beitraege?: readonly { period_id: string | null; status: string }[];
}): { pflicht: PruefPunkt[]; wichtig: PruefPunkt[]; hinweise: PruefPunkt[]; ibanUnbekannt: boolean } {
  const { deal, period, talent, empfaenger, settings } = input;
  const ibanUnbekannt = input.iban === undefined;
  const res = checkInvoiceRequest({
    recipient: { legalName: empfaenger.name === "–" ? null : empfaenger.name, addressLines: empfaenger.zeilen, country: empfaenger.country, vatId: empfaenger.vatId },
    issuer: {
      country: talent?.country ?? null,
      smallBusiness: !!talent?.small_business,
      vatId: talent?.vat_id ?? null,
      taxNumber: talent?.tax_number ?? null,
      iban: ibanUnbekannt ? "unbekannt" : input.iban ?? null,
      addressLines: [talent?.street, [talent?.postal_code, talent?.city].filter(Boolean).join(" "), talent?.country].filter((x): x is string => !!x && !!x.trim()),
    },
    // Ohne Land der Agentur keine Auslandsprüfung (kein fester Rückfall auf ein Land, Befund 23)
    orgCountry: settings?.country ?? talent?.country ?? "",
    amountCents: deal.model === "commission_only" ? null : period.amount_cents,
    commissionOnly: deal.model === "commission_only",
    currency: deal.currency,
    allowedCurrencies: (settings?.currencies?.length ? settings.currencies : [settings?.currency ?? deal.currency]) as never,
    visibleInPortal: deal.visible_to_talent,
    hasPortalAccount: input.hasPortalAccount,
  });
  const where = (code: RequestCheckCode): PruefPunkt["wo"] => {
    if (code.startsWith("RECIPIENT")) return empfaenger.wo;
    if (code.startsWith("ISSUER") || code === "NO_PORTAL_ACCOUNT") return { label: t("Creator-Profil", "Creator profile"), to: `/agentur/creator/${deal.talent_id}` };
    return { label: t("Kooperation bearbeiten", "Edit deal"), to: `/agentur/kooperationen/${deal.id}/bearbeiten` };
  };
  const toPoint = (f: { code: string }): PruefPunkt => ({ code: f.code as RequestCheckCode, text: CHECK_TEXT[f.code as RequestCheckCode] ?? f.code, wo: where(f.code as RequestCheckCode) });
  const wichtig = res.important.map(toPoint);
  // Leistung noch nicht erbracht: Content weder freigegeben noch veröffentlicht, oder der Monat liegt in der Zukunft (Befund 13).
  // Ausfallhonorar, Minderung und Nachkauf sind ohne Content abzurechnen.
  const ohneLeistung = monatMitAusfall(period) || period.kind === "follow_up";
  // Prüf-Chat CP-023 (02.11.2026): Der Dialog sagte „weder freigegeben noch veröffentlicht“, obwohl der Reel freigegeben und live
  // war (nur die Story fehlte). Freigabe und Veröffentlichung stehen bei Beiträgen am Beitrag, nicht am Monat.
  const eigene = (input.beitraege ?? []).filter((b) => (b.period_id === period.id || !b.period_id) && b.status !== "cancelled");
  const fertig = eigene.filter((b) => b.status === "approved" || b.status === "live");
  const erbracht = ohneLeistung || !!period.content_approved_at || !!period.published_on || (!!input.stand && ["published", "reporting", "invoicing", "done"].includes(input.stand))
    || (eigene.length > 0 && fertig.length === eigene.length);
  const zum = { label: t("Content und Veröffentlichung", "Content and publication"), to: `/agentur/kooperationen/${deal.id}/content?monat=${period.month.slice(0, 7)}` };
  if (input.stand !== undefined && !erbracht && fertig.length > 0) {
    wichtig.push({ code: "LEISTUNG_OFFEN", text: t(`Erst ${fertig.length} von ${eigene.length} Beiträgen sind freigegeben oder live.`, `Only ${fertig.length} of ${eigene.length} posts are approved or live.`), wo: zum });
  } else if (input.stand !== undefined && !erbracht) {
    wichtig.push({ code: "LEISTUNG_OFFEN", text: t(`Der Content ist weder freigegeben noch veröffentlicht${input.stand ? t(` (Leistungsstand: ${statusDef(input.stand).label})`, ` (service status: ${statusDef(input.stand).label})`) : ""}.`, `The content is neither approved nor published${input.stand ? t(` (Leistungsstand: ${statusDef(input.stand).label})`, ` (service status: ${statusDef(input.stand).label})`) : ""}.`), wo: zum });
  }
  if (input.today && !ohneLeistung && period.month.slice(0, 7) > input.today.slice(0, 7)) {
    wichtig.push({ code: "MONAT_ZUKUNFT", text: t(`Der Leistungsmonat ${monthText(period.month.slice(0, 7))} liegt in der Zukunft.`, `The service month ${monthText(period.month.slice(0, 7))} is in the future.`), wo: null });
  }
  return { pflicht: res.required.map(toPoint), wichtig, hinweise: res.hints.map(toPoint), ibanUnbekannt };
}

export interface Vorschlag {
  description: string;
  quantity: number;
  /** Betrag, den der Creator stellt: über die Agentur sein Anteil (nach Provision, KZ-05), direkt der Betrag an die Marke; null bei reiner Provision */
  amount_cents: number | null;
  commission_free: boolean;
}

/**
 * Vorgeschlagene Positionen für die Rechnung des Creators: Honorar und Zusatzpositionen des Monats nach Agenturanteil.
 * Direkt an die Marke stellt der Creator den vollen Betrag (nach einem Abzug der Marke); die Provision berechnet die Agentur ihm getrennt.
 * Die Beträge sind die Rechnungsbeträge des Creators: sein Assistent zieht davon nichts mehr ab (GELD-1).
 */
export function vorschlaege(deal: DealFull, period: PeriodFull, periods: readonly PeriodFull[], items: readonly ItemRow[], ratePercentIn: number | null,
  opts: { nurNach?: string | null } = {}): Vorschlag[] {
  const direkt = routeOf(deal) === "direct_to_brand";
  const own = [...periods].filter((p) => p.deal_id === deal.id);
  // Nachberechnung (Nachkauf nach der Rechnung des Creators): nur die danach angelegten Positionen
  const nach = opts.nurNach ?? null;
  // Positionen ohne Monat gehören zum ersten Monat der Kooperation (extraInPeriod, wie Aufteilung und Rechnung an die Marke)
  const extras = items.filter((i) => i.deal_id === deal.id && extraInPeriod(i.period_id, period.id, own) && (!nach || (!!i.created_at && i.created_at > nach)));
  const out: Vorschlag[] = [];
  const baseText = leistungsText(deal, period);
  if (deal.model === "commission_only" && !nach) out.push({ description: `${baseText} · ${monthText(period.month.slice(0, 7))}`, quantity: 1, amount_cents: null, commission_free: false });
  const lines = monthLines(deal, nach ? 0 : period.amount_cents, extras, baseText);
  if (!lines.length) return out;
  const abzug = brandDiscountFromRow(deal);
  const posten = lines.map((l) => ({ description: l.description, amountCents: Math.round(l.unitPriceCents * l.quantity), commissionFree: !!l.commissionFree }));
  // Direkt an die Marke stellt der Creator, was die Marke zahlt: das Honorar nach ihrem Abzug (0060), ohne Provisionsabzug
  if (direkt) return [...out, ...brandNetLines(posten, abzug).map((l) => ({ description: l.description, quantity: 1, amount_cents: l.amountCents, commission_free: l.commissionFree }))];
  if (ratePercentIn == null) return [...out, ...lines.map((l) => ({ description: l.description, quantity: 1, amount_cents: null, commission_free: !!l.commissionFree }))];
  // Über die Agentur: Anteil des Creators nach Satz oder Staffel und nach dem Abzug der Marke (shareOfHonorar, wie Aufteilung und Wächter)
  // Im Nachlauf nach Austritt gilt der Satz des Monats ohne Staffel (0074), wie in der Aufteilung
  const nachlauf = deal.post_term ? dealBpForMonth(deal, period.month.slice(0, 7)) : null;
  const rate = { percent: ratePercentIn, tiers: nachlauf != null ? null : tiersFromJson(deal.commission_tiers) };
  return [...out, ...creatorShareLines(posten, rate, abzug).map((l) => ({ description: l.description, quantity: 1, amount_cents: l.amountCents, commission_free: l.commissionFree }))];
}

/** Erklärung über den Vorschlägen im Dialog „Rechnung anfordern“ je Abrechnungsweg (GELD-17). */
export function vorschlagsHinweis(deal: Pick<DealFull, "invoice_route" | "route">): string {
  return routeOf(deal) === "direct_to_brand"
    ? t("Vorschläge für die Rechnung des Creators an die Marke (voller Betrag, ohne Provisionsabzug; die Provision stellt die Agentur ihm getrennt in Rechnung). Der Creator kann sie ändern, ergänzen oder löschen.", "Suggestions for the creator's invoice to the brand (full amount, without commission deduction; the agency invoices the commission separately). The creator can change, add or delete them.")
    : t("Vorschläge für die Rechnung des Creators an die Agentur (sein Anteil nach Abzug der Provision; sein Assistent zieht nichts mehr ab). Der Creator kann sie ändern, ergänzen oder löschen.", "Suggestions for the creator's invoice to the agency (their share after the commission; their assistant deducts nothing more). The creator can change, add or delete them.");
}

/**
 * Artikel aus dem Katalog als Vorschlag: der Katalogpreis ist ein Preis an die Marke; über die Agentur schlägt der Dialog den
 * Anteil des Creators vor (Satz des Monats), damit alle Vorschläge dieselbe Art Betrag sind (GELD-1).
 */
export function artikelVorschlagCents(deal: Pick<DealFull, "invoice_route" | "route">, priceCents: number | null, commissionable: boolean, ratePercent: number | null): number | null {
  if (priceCents == null || !commissionable || routeOf(deal) === "direct_to_brand" || ratePercent == null) return priceCents;
  return priceCents - Math.round((priceCents * ratePercent) / 100);
}

/** Späteste lebende Rechnung des Creators im Monat; danach angelegte Positionen sind ein Nachkauf, der erneut anzufordern ist (0060). */
export function nachkaufSeit(m: MonatInfo, items: readonly ItemRow[]): { seit: string; positionen: ItemRow[] } | null {
  const rechnungen = m.invoices.filter((e) => isCreatorInvoice(e.row) && ["submitted", "approved", "sent", "paid"].includes(e.row.status));
  if (!rechnungen.length) return null;
  const seit = rechnungen.map((e) => e.row.created_at as string).sort().at(-1)!;
  const positionen = items.filter((i) => i.period_id === m.period.id && !!i.created_at && i.created_at > seit);
  return positionen.length ? { seit, positionen } : null;
}

// ── Rechnungen (Rückfall ohne Fachlogik-Konfiguration) ──────────────────────

const INVOICE_STATUS_PILL: Record<string, Pill> = {
  get draft() { return pill(t("Entwurf", "Draft"), "neutral"); },
  get submitted() { return pill(t("In Prüfung", "In review"), "warning"); },
  get revision_requested() { return pill(t("Korrektur nötig", "Correction needed"), "warning"); },
  get rejected() { return pill(t("Abgelehnt", "Declined"), "error"); },
  get approved() { return pill(t("Freigegeben", "Approved"), "info"); },
  get sent() { return pill(t("Offen", "Open"), "info"); },
  get paid() { return pill(t("Bezahlt", "Paid"), "success"); },
  get cancelled() { return pill(t("Storniert", "Cancelled"), "neutral"); },
  get withdrawn() { return pill(t("Zurückgezogen", "Withdrawn"), "neutral"); },
  get discarded() { return pill(t("Verworfen", "Discarded"), "neutral"); },
};
export function invoicePillFallback(status: string): Pill {
  return INVOICE_STATUS_PILL[status] ?? pill(status, "neutral");
}
export const INVOICE_KIND_LABEL: Record<string, string> = {
  get talent_to_agency() { return t("Rechnung des Creators an die Agentur", "Creator's invoice to the agency"); },
  get agency_to_brand() { return t("Rechnung an die Marke", "Invoice to the brand"); },
  get talent_to_brand() { return t("Rechnung des Creators an die Marke", "Creator's invoice to the brand"); },
  get agency_to_talent() { return t("Provisionsrechnung an den Creator", "Commission invoice to the creator"); },
};

/** Prozent mit bis zu zwei Nachkommastellen im Zahlenformat der Agentur („30 %“, „12,5 %“) */
export function prozent(v: number): string {
  return `${new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 2 }).format(v)} %`;
}

// ── Kampagnen (Befund 22) ───────────────────────────────────────────────────

export interface KampagnenStand {
  kampagne: CampaignRow;
  zeilen: KoopZeile[];
  creator: number;
  /** Summe der Honorare der Kooperationen in der Währung der Kampagne */
  honorarCents: number;
  /** Budget minus Honorare (null ohne Budget) */
  restCents: number | null;
  offeneMonate: number;
}

export function kampagnenStand(kampagne: CampaignRow, zeilen: readonly KoopZeile[]): KampagnenStand {
  const eigene = zeilen.filter((z) => z.deal.campaign_id === kampagne.id && !z.archiviert);
  const honorar = eigene.reduce((a, z) => a + (z.honorarCents ?? 0), 0);
  return {
    kampagne, zeilen: eigene, creator: new Set(eigene.map((z) => z.deal.talent_id)).size, honorarCents: honorar,
    restCents: kampagne.budget_cents == null ? null : kampagne.budget_cents - honorar,
    offeneMonate: eigene.reduce((a, z) => a + z.monate.filter((m) => !m.done).length, 0),
  };
}

/**
 * Monate, die in eine Sammelrechnung an die Marke passen: Kooperationen der Marke (optional nur einer Kampagne) über die
 * Agentur, gleiche Währung, Monat mit Honorar, noch keine lebende Rechnung an die Marke und nicht storniert.
 */
export interface SammelMonat {
  zeile: KoopZeile;
  monat: MonatInfo;
  betragCents: number;
  extrasCents: number;
}
export function sammelMonate(zeilen: readonly KoopZeile[], opts: { brandId: string; currency: string; campaignId?: string | null }): SammelMonat[] {
  const out: SammelMonat[] = [];
  for (const z of zeilen) {
    if (z.deal.brand_id !== opts.brandId || z.deal.currency !== opts.currency || z.archiviert || NO_MONEY.has(z.deal.model) || z.deal.model === "commission_only") continue;
    if (opts.campaignId && z.deal.campaign_id !== opts.campaignId) continue;
    if (routeOf(z.deal) === "direct_to_brand") continue;
    for (const m of z.monate) {
      // Positionen ohne Monat gehören zum ersten Monat der Kooperation (extraInPeriod)
      const extras = z.items.filter((i) => i.deal_id === z.deal.id && extraInPeriod(i.period_id, m.period.id, z.monate.map((x) => x.period))).reduce((a, i) => a + i.amount_cents, 0);
      // Nachkauf-Monate haben nur Positionen (0060)
      if (monatStorniert(m.period) || !((m.period.amount_cents ?? 0) + extras)) continue;
      if (m.invoices.some((e) => isBrandInvoice(e.row) && !["withdrawn", "discarded", "cancelled", "rejected"].includes(e.row.status))) continue;
      out.push({ zeile: z, monat: m, betragCents: m.period.amount_cents ?? 0, extrasCents: extras });
    }
  }
  return out.sort((a, b) => a.monat.month.localeCompare(b.monat.month) || a.zeile.talentName.localeCompare(b.zeile.talentName, "de"));
}

// ── Ausgleich mit dem Creator im Monat (Korrekturrunde 3) ──────────────────

/**
 * Beschriftung des Bereichs „Ausgleich mit dem Creator“ (PRAXIS-R3-3): liegt ein Beleg vor, bestimmt er das Etikett (Gutschrift nur,
 * wenn der Beleg eine ist); sonst die Vereinbarung am Creator (Gutschriftverfahren) bzw. der Abrechnungsweg.
 */
export function creatorBereichLabel(a: { direkt: boolean; gutschriftVereinbart: boolean; beleg?: Pick<InvoiceRow, "kind" | "self_billed"> | null }): string {
  if (a.direkt) return t("Provisionsrechnung an den Creator", "Commission invoice to the creator");
  const gutschrift = a.beleg ? !!a.beleg.self_billed : a.gutschriftVereinbart;
  return gutschrift ? t("Gutschrift an den Creator", "Self-billing invoice to the creator") : t("Rechnung des Creators", "Creator's invoice");
}

/**
 * Zweite Hälfte der Buchungszeile über die Agentur (GELD-R3-10): derselbe Stand wie „Stand je Bereich“. Vermerkte Auszahlung
 * (admin_payment_confirmed_at) heißt „ausgezahlt, Bestätigung des Creators offen“, eine anteilige Auszahlung nennt Teil und Rest.
 */
export function creatorAusgleichText(a: { talentPaidAt: string | null; invoiced: boolean;
  rows: readonly Pick<InvoiceRow, "kind" | "status" | "gross_cents" | "paid_cents" | "admin_payment_confirmed_at" | "currency">[];
  fmtDate: (d: string) => string; fmtMoney: (c: number, cur: string) => string }): string {
  if (a.talentPaidAt) return t(`Auszahlung an den Creator am ${a.fmtDate(a.talentPaidAt)}`, `Payout to the creator on ${a.fmtDate(a.talentPaidAt)}`);
  const live = a.rows.filter((r) => r.kind === "talent_to_agency" && (r.status === "approved" || r.status === "sent"));
  const confirmed = live.find((r) => !!r.admin_payment_confirmed_at);
  if (confirmed) {
    const on = a.fmtDate(confirmed.admin_payment_confirmed_at!);
    return t(`Ausgezahlt am ${on}, Bestätigung des Creators offen`, `Paid out on ${on}, creator's confirmation open`);
  }
  const partial = live.find((r) => Number(r.paid_cents ?? 0) > 0 && Number(r.paid_cents ?? 0) < Number(r.gross_cents ?? 0));
  if (partial) {
    const paid = Number(partial.paid_cents);
    const rest = Number(partial.gross_cents ?? 0) - paid;
    return t(`Anteilig ausgezahlt ${a.fmtMoney(paid, partial.currency)}, Rest ${a.fmtMoney(rest, partial.currency)} offen`,
      `Partly paid out ${a.fmtMoney(paid, partial.currency)}, rest ${a.fmtMoney(rest, partial.currency)} open`);
  }
  return a.invoiced ? t("Rechnung des Creators liegt vor, Auszahlung offen", "Creator's invoice available, payout open") : t("Rechnung des Creators fehlt", "Creator invoice missing");
}

/** Warum ein übernommener Monat nicht angefordert wird: je Stand aus dem bisherigen System (PRAXIS-R3-1). */
export function uebernommenGrund(state: "invoiced" | "brand_paid" | "paid"): string {
  return state === "paid" ? t("Der Monat ist im bisherigen System vollständig bezahlt (übernommen).", "The month has been fully paid in the previous system (taken over).")
    : state === "brand_paid" ? t("Die Marke hat diesen Monat im bisherigen System bezahlt (übernommen); offen ist nur die Auszahlung an den Creator. Seine offene Rechnung als übernommene Rechnung unter Rechnungen erfassen.", "The brand paid this month in the previous system (taken over); only the payout to the creator is open. Record the creator's open invoice as a taken-over invoice under Invoices.")
    : t("Der Monat ist im bisherigen System abgerechnet (übernommen). Offene Rechnungen von dort als übernommene Rechnungen unter Rechnungen erfassen, keine neue anfordern.", "The month has been invoiced in the previous system (taken over). Record open invoices from there as taken-over invoices under Invoices; do not request a new one.");
}

/** Hinweis oben auf der Kooperation: wie viele Monate schon im bisherigen System abgerechnet sind (Verb nach Anzahl, PRAXIS-R3-8). */
export function uebernommenHinweis(n: number): string {
  if (n <= 0) return "";
  return n === 1
    ? t(" 1 Monat ist dort schon abgerechnet und wird hier nicht angefordert; offene Beträge stehen als übernommene Rechnungen unter Rechnungen.", " 1 month is already invoiced there and is not requested here; open amounts appear as taken-over invoices under Invoices.")
    : t(` ${n} Monate sind dort schon abgerechnet und werden hier nicht angefordert; offene Beträge stehen als übernommene Rechnungen unter Rechnungen.`, ` ${n} months are already invoiced there and are not requested here; open amounts appear as taken-over invoices under Invoices.`);
}

/**
 * 0394 (K-526): eigener Zwischenstand der Kooperation, wenn er zum aktuellen Oberstand gehört; Anzeige „Ware beim Creator seit
 * 12.10.2026“. Fachlich zählt weiter nur der Oberstand (Gruppe, Rang, Board).
 */
export function zwischenstand(deal: Pick<DealFull, "status" | "substep_id" | "substep_since">, substeps: readonly Substep[] | undefined, lang: "de" | "en" = currentLang() === "en" ? "en" : "de"): { id: string; label: string; seit: string | null; text: string } | null {
  if (!deal.substep_id) return null;
  const s = (substeps ?? []).find((x) => x.id === deal.substep_id);
  if (!s || s.parent_status !== deal.status) return null;
  const label = (lang === "en" && s.label_en?.trim()) || s.label_de;
  const seit = deal.substep_since ?? null;
  return { id: s.id, label, seit, text: seit ? t(`${label} seit ${tag(seit)}`, `${label} since ${tag(seit)}`) : label };
}
/** Zwischenstände, die zu einem Oberstand passen (ohne archivierte), nach Reihenfolge */
export function zwischenstaendeFuer(status: string, substeps: readonly Substep[] | undefined): Substep[] {
  return (substeps ?? []).filter((s) => s.parent_status === status && !s.archived).sort((a, b) => a.sort - b.sort || a.label_de.localeCompare(b.label_de, "de"));
}
/**
 * Vorschläge für eigene Zwischenstände (K-526, Arbeitsschritte aus der Praxis), je Oberstand. Die Agentur übernimmt sie mit einem
 * Klick oder legt eigene an.
 */
export const ZWISCHENSTAND_VORSCHLAEGE: readonly { status: DealStatus; de: string; en: string }[] = [
  { status: "contract", de: "Vertrag unterschrieben", en: "Contract signed" },
  { status: "concept", de: "Briefing in Arbeit", en: "Briefing in progress" },
  { status: "concept", de: "Briefing wartet auf Marke", en: "Briefing awaiting brand" },
  { status: "concept", de: "Konzept wartet auf Marke", en: "Concept awaiting brand" },
  { status: "production", de: "Ware unterwegs zum Creator", en: "Product on its way to creator" },
  { status: "production", de: "Ware beim Creator", en: "Product with creator" },
  { status: "content_approval", de: "Content in Korrektur", en: "Content in revision" },
  { status: "content_approval", de: "Marke prüft", en: "Brand reviewing" },
  { status: "blocked", de: "Marke pausiert", en: "Brand paused" },
  { status: "reporting", de: "Folgekooperation angefragt", en: "Follow-up requested" },
];
